import { Capacitor, registerPlugin } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { rememberSuspended } from './components/moderation/suspended';
import { InviteRequiredError, isInviteRequiredBody, pendingMarkInvite, requestInviteCode } from './components/invite/inviteRequired';
export { InviteRequiredError } from './components/invite/inviteRequired';

// I bundlat Capacitor-läge (capacitor://) fungerar inte relativa URL:er — använd absoluta bas-URL:er.
const IS_CAPACITOR_BUNDLE = typeof window !== 'undefined' &&
  (window.location.protocol === 'capacitor:' ||
   !!(window as any).Capacitor?.isNativePlatform?.());

/** Om VITE_API_URL saknas i prod byggs relativa /graphql → Firebase index.html → "Unexpected token '<'". */
const PUBLIC_API_BASE_FALLBACK = 'https://api.qvenari.com';
/** Qmeet måste inte anropa relativa /qmeet/... på qvenari.com (Firebase → ev. Basic Auth / HTML). */
const PUBLIC_QMEET_BASE_FALLBACK = 'https://qmeet.qvenari.com';
/** Qid web-host (samma som normalizeQidBrowserBy för qid.qvenari.com) — används om VITE_QID_URL saknas i prod. */
const PUBLIC_QID_BASE_FALLBACK = 'https://qid-qvenari.web.app';

// Production base URLs from env vars (set when deployed to Firebase, empty in local dev)
const PROD_API_BASE: string = (import.meta.env.VITE_API_URL as string) || '';
/** Qid i browser — undvik qid.qvenari.com tills DNS pekar på Firebase (annars Google 404 → failed to fetch). */
function normalizeQidBrowserBase(url: string): string {
  const u = url.trim();
  if (!u) return '';
  try {
    const parsed = new URL(u);
    if (parsed.hostname.toLowerCase() === 'qid.qvenari.com') {
      return PUBLIC_QID_BASE_FALLBACK;
    }
  } catch {
    /* ignore */
  }
  return u.replace(/\/$/, '');
}
const PROD_QID_BASE: string = normalizeQidBrowserBase((import.meta.env.VITE_QID_URL as string) || '');
const PROD_QMEET_BASE: string = (import.meta.env.VITE_QMEET_URL as string) || '';

function trimBase(u: string): string {
  return u.replace(/\/$/, '');
}

/** Capacitor: VITE_CAPACITOR_* om satta, annars VITE_*, annars publikt API i prod (aldrig localhost på telefon). */
export const NATIVE_API_BASE = trimBase(
  ((import.meta.env.VITE_CAPACITOR_API_URL as string) || '').trim() ||
    PROD_API_BASE ||
    (import.meta.env.PROD ? PUBLIC_API_BASE_FALLBACK : 'http://localhost:3500'),
);
const NATIVE_AUTH_BASE = trimBase(
  ((import.meta.env.VITE_CAPACITOR_QID_URL as string) || '').trim() ||
    PROD_QID_BASE ||
    'http://localhost:3400',
);
export const NATIVE_QMEET_BASE = trimBase(
  ((import.meta.env.VITE_CAPACITOR_QMEET_URL as string) || '').trim() ||
    PROD_QMEET_BASE ||
    (import.meta.env.PROD ? PUBLIC_QMEET_BASE_FALLBACK : 'http://localhost:3200'),
);

const API_URL = IS_CAPACITOR_BUNDLE
  ? `${NATIVE_API_BASE}/graphql`
  : PROD_API_BASE
    ? `${PROD_API_BASE}/graphql`
    : import.meta.env.DEV
      ? '/graphql'
      : `${PUBLIC_API_BASE_FALLBACK}/graphql`;
export const API_KEY = 'dev-secret';

/** Auth paths that ska till Qvenari-API (BFF), inte direkt till Qid. */
function isQvenariAuthBffPath(path: string): boolean {
  if (!path.startsWith('/auth/')) return false;
  if (path === '/auth/config' || path === '/auth/token') return true;
  if (path === '/auth/me') return true;
  if (path === '/auth/direct-login' || path === '/auth/login') return true;
  if (path === '/auth/register' || path === '/auth/register-with-invite') return true;
  if (path === '/auth/invite') return true; // IK1
  if (path.startsWith('/auth/verify/')) return true;
  return false;
}

// Löser relativa URL:er för bundlat Capacitor-läge eller produktionsmiljö
export function resolveUrl(path: string): string {
  if (IS_CAPACITOR_BUNDLE) {
    if (path === '/graphql') return `${NATIVE_API_BASE}/graphql`;
    if (path === '/auth/config' || path === '/auth/token' || path === '/auth/me') return `${NATIVE_API_BASE}${path}`;
    // IK1: registrering och invitekod kontrolleras i Qvenari-API:t, aldrig direkt mot Qid.
    if (path === '/auth/register' || path === '/auth/invite') return `${NATIVE_API_BASE}${path}`;
    if (path.startsWith('/auth/')) return `${NATIVE_AUTH_BASE}${path}`;
    if (path.startsWith('/qmeet/')) return `${NATIVE_QMEET_BASE}${path.replace(/^\/qmeet/, '')}`;
    if (path.startsWith('/api/') || path.startsWith('/uploads/') || path.startsWith('/checkout/') || path.startsWith('/oauth/'))
      return `${NATIVE_API_BASE}${path}`;
    return path;
  }
  if (path.startsWith('/auth/')) {
    if (isQvenariAuthBffPath(path)) {
      const webApiBase = trimBase(PROD_API_BASE || (import.meta.env.PROD ? PUBLIC_API_BASE_FALLBACK : ''));
      if (webApiBase) return `${webApiBase}${path}`;
      return path;
    }
    const qidBase = trimBase(PROD_QID_BASE || (import.meta.env.PROD ? PUBLIC_QID_BASE_FALLBACK : ''));
    if (qidBase) return `${qidBase}${path}`;
    return path;
  }
  const webQmeetBase = (PROD_QMEET_BASE || (import.meta.env.PROD ? PUBLIC_QMEET_BASE_FALLBACK : '')).replace(/\/$/, '');
  if (webQmeetBase && path.startsWith('/qmeet/')) return `${webQmeetBase}${path.replace(/^\/qmeet/, '')}`;
  const webApiBase = (PROD_API_BASE || (import.meta.env.PROD ? PUBLIC_API_BASE_FALLBACK : '')).replace(/\/$/, '');
  if (webApiBase && path === '/graphql') return `${webApiBase}/graphql`;
  if (webApiBase && (path.startsWith('/api/') || path.startsWith('/uploads/') || path.startsWith('/checkout/') || path.startsWith('/oauth/')))
    return `${webApiBase}${path}`;
  return path;
}

/** På qvenari.com / i Capacitor måste /uploads/... peka på API-origin, inte webb-/Firebase-host. */
export function absolutizeUploadUrl(url: string): string {
  if (!url || url.startsWith('http')) return url;
  const base = trimBase(
    IS_CAPACITOR_BUNDLE
      ? NATIVE_API_BASE
      : (PROD_API_BASE || (import.meta.env.PROD ? PUBLIC_API_BASE_FALLBACK : '')),
  );
  if (base && url.startsWith('/uploads/')) return `${base}${url}`;
  return url;
}

export function qmeetBase(): string {
  if (IS_CAPACITOR_BUNDLE) return NATIVE_QMEET_BASE;
  if (PROD_QMEET_BASE) return PROD_QMEET_BASE;
  if (import.meta.env.PROD) return PUBLIC_QMEET_BASE_FALLBACK;
  return '/qmeet';
}

/** Absolut URL till Qmeet REST, t.ex. qmeetApi('/api/meetings/x/join'). */
export function qmeetApi(apiPath: string): string {
  const p = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
  return resolveUrl(`/qmeet${p}`);
}

const DEFAULT_PUBLIC_WEB_APP = 'https://qvenari.com';

function safePublicWebOriginFromString(raw: string): string | null {
  const t = raw.trim().replace(/\/$/, '');
  if (!t) return null;
  try {
    const u = new URL(t.startsWith('http') ? t : `https://${t}`);
    const h = u.hostname.toLowerCase();
    // Endast riktiga webbhosts — aldrig api.* (där Basic Auth / gateway ofta frågar efter lösenord)
    if (h === 'qvenari.com' || h === 'www.qvenari.com') return `${u.protocol}//${h}`;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Publik webbadress för delning/QR (jaktmöte m.m.).
 * Tvingar qvenari.com om användaren råkar köra UI från api.qvenari.com eller om VITE_PUBLIC_APP_URL pekar fel.
 */
export function publicWebAppUrl(): string {
  const fromEnv = safePublicWebOriginFromString((import.meta.env.VITE_PUBLIC_APP_URL as string) || '');
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname.toLowerCase();
    if (h === 'qvenari.com' || h === 'www.qvenari.com') return window.location.origin;
    if (h === 'api.qvenari.com' || (h.startsWith('api.') && h.endsWith('.qvenari.com'))) return DEFAULT_PUBLIC_WEB_APP;
  }

  if (import.meta.env.PROD) return DEFAULT_PUBLIC_WEB_APP;
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return DEFAULT_PUBLIC_WEB_APP;
}

// ─── AUTH HELPERS ──────────────────────────────────────────────

function getAccessToken(): string | null {
  return localStorage.getItem('qvenari_access_token');
}

function setTokens(access_token: string, id_token?: string, refresh_token?: string) {
  localStorage.setItem('qvenari_access_token', access_token);
  if (id_token) localStorage.setItem('qvenari_id_token', id_token);
  if (refresh_token) localStorage.setItem('qvenari_refresh_token', refresh_token);
}

function clearTokens() {
  localStorage.removeItem('qvenari_access_token');
  localStorage.removeItem('qvenari_id_token');
  localStorage.removeItem('qvenari_refresh_token');
  localStorage.removeItem('qvenari_user');
}

export interface QidUser {
  sub: string;
  email: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  locale?: string;
  picture?: string;
  verification_level?: number;
  auth_method?: string;
}

/**
 * Namnet som visas för andra i jakten (chatt, positioner). Ett sparat namn används bara om det
 * inte är platshållaren "Jägare"; annars Qid-namnet, och i sista hand ett namn ur e-posten
 * ("lars.roth@x.se" → "Lars Roth") — samma ordning som API:ts pickDisplayName.
 */
export function huntDisplayName(user: Pick<QidUser, 'email' | 'name' | 'given_name' | 'family_name'> | null | undefined): string {
  const saved = (() => { try { return localStorage.getItem('qvenari_user_name')?.trim(); } catch { return undefined; } })();
  const generic = (n?: string | null) => !n || ['jägare', 'jagare', 'gäst', 'gast'].includes(n.trim().toLowerCase());
  if (!generic(saved)) return saved!;
  const qid = user?.name?.trim() || [user?.given_name, user?.family_name].filter(Boolean).join(' ');
  if (!generic(qid)) return qid;
  const local = user?.email?.split('@')[0] ?? '';
  const words = local.split(/[._-]+/).filter(w => w && !/^\d+$/.test(w));
  return words.length ? words.map(w => w[0].toUpperCase() + w.slice(1)).join(' ') : 'Jägare';
}

// ─── QID OIDC (webb + iOS) ────────────────────────────────────

/** iOS-appens tillbakalänk; registrerad som redirect_uri för klienten "qvenari" i Qid. */
const NATIVE_CALLBACK_SCHEME = 'se.qvenari.app';
const NATIVE_REDIRECT_URI = `${NATIVE_CALLBACK_SCHEME}://auth/callback`;

/** Nativ plugin i ios/App/App/ViewController.swift (QidAuthPlugin). */
const QidAuth = registerPlugin<{
  start(options: { url: string; callbackScheme: string }): Promise<{ url: string }>;
}>('QidAuth');

/**
 * Android (AN1): Qid i en Chrome Custom Tab (Google tillåter inte inloggning i WebView). Qid
 * skickar tillbaka till se.qvenari.app://auth/callback, som MainActivity tar emot (intent-filter)
 * och @capacitor/app lämnar som appUrlOpen. Stängs fliken utan svar räknas det som avbrutet.
 */
let cancelCustomTabLogin: (() => void) | null = null;

export function loginInCustomTab(url: string): Promise<string> {
  // Ett nytt försök ersätter ett som aldrig fick svar (fliken lämnad öppen i bakgrunden).
  cancelCustomTabLogin?.();
  return new Promise((resolve, reject) => {
    let done = false;
    const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];
    const finish = (fn: () => void) => {
      if (done) return;
      done = true;
      cancelCustomTabLogin = null;
      for (const h of handles) void h.then(x => x.remove()).catch(() => {});
      fn();
    };
    cancelCustomTabLogin = () => finish(() => reject(new Error('Inloggningen avbröts')));
    handles.push(CapApp.addListener('appUrlOpen', e => {
      if (!e.url?.startsWith(NATIVE_REDIRECT_URI)) return;
      void Browser.close().catch(() => {});
      finish(() => resolve(e.url));
    }));
    // Fliken stängs också när appen öppnas av tillbakalänken; vänta lite på appUrlOpen innan det
    // räknas som att användaren stängde fliken.
    handles.push(Browser.addListener('browserFinished', () => {
      setTimeout(() => finish(() => reject(new Error('Inloggningen avbröts'))), 1500);
    }));
    Browser.open({ url, presentationStyle: 'fullscreen' }).catch(err => finish(() => reject(err)));
  });
}

type OidcConfig = { client_id: string; authorization_endpoint: string; scope: string };

/** Inloggningssättet Qid går direkt till (Qids eget formulär visas inte). */
export type QidProvider = 'google' | 'apple';

function buildAuthorizeUrl(config: OidcConfig, redirectUri: string, state: string, provider: QidProvider = 'google'): string {
  const url = new URL(config.authorization_endpoint);
  url.searchParams.set('client_id', config.client_id);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', config.scope);
  url.searchParams.set('state', state);
  // Gå direkt till Google/Apple — Qids eget inloggningsformulär visas inte (se Qid LoginPage).
  url.searchParams.set('provider', provider);
  // Qids sida (om den visas, t.ex. efter avbruten Google-inloggning) får samma läge som Qvenari.
  url.searchParams.set('ui_theme', localStorage.getItem('qvenari_theme') === 'dark' ? 'dark' : 'light');
  return url.toString();
}

async function exchangeCode(code: string, redirectUri: string): Promise<QidUser> {
  const res = await fetch(resolveUrl('/auth/token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error ?? 'Token exchange failed');
  }

  const tokens = await res.json() as { access_token: string; id_token?: string; refresh_token?: string };
  setTokens(tokens.access_token, tokens.id_token, tokens.refresh_token);

  // Fetch user info
  const headers: Record<string, string> = { Authorization: `Bearer ${tokens.access_token}` };
  if (tokens.id_token) headers['X-Id-Token'] = tokens.id_token;
  const userRes = await fetch(resolveUrl('/auth/me'), { headers });

  if (!userRes.ok) {
    const err = await userRes.json().catch(() => ({})) as { error?: string; suspended?: boolean };
    if (err.suspended) { clearTokens(); rememberSuspended(err.error ?? 'Kontot är avstängt'); throw new Error(err.error ?? 'Kontot är avstängt'); }
    // IK1: nytt konto utan invitekod – tokens behålls så att koden kan anges utan ny inloggning.
    if (isInviteRequiredBody(err)) { requestInviteCode(); throw new InviteRequiredError(err.error); }
    throw new Error('Failed to fetch user info');
  }
  const user = await userRes.json() as QidUser;
  auth.setUser(user);
  return user;
}

export const auth = {
  getAccessToken,
  setTokens,
  clearTokens,

  isLoggedIn: () => !!getAccessToken(),

  getUser: (): QidUser | null => {
    const raw = localStorage.getItem('qvenari_user');
    return raw ? JSON.parse(raw) : null;
  },

  setUser: (user: QidUser) => {
    localStorage.setItem('qvenari_user', JSON.stringify(user));
  },

  // Get OIDC config from backend
  getConfig: async () => {
    const res = await fetch(resolveUrl('/auth/config'));
    return res.json() as Promise<{
      issuer: string;
      client_id: string;
      authorization_endpoint: string;
      redirect_uri: string;
      scope: string;
    }>;
  },

  // Start OIDC login — redirect to Qid
  login: async (provider: QidProvider = 'google') => {
    const config = await auth.getConfig();
    const state = crypto.randomUUID();
    sessionStorage.setItem('oidc_state', state);
    window.location.href = buildAuthorizeUrl(config, config.redirect_uri, state, provider);
  },

  /** Är Logga in med Apple påslaget i Qid? (Knappen visas bara då.) */
  appleEnabled: async (): Promise<boolean> => {
    try {
      const res = await fetch(resolveUrl('/auth/social/providers'));
      const data = await res.json() as { providers?: { id: string; enabled: boolean }[] };
      return !!data.providers?.some(p => p.id === 'apple' && p.enabled);
    } catch {
      return false;
    }
  },

  // Inloggning via Qid (där Google och Apple finns). Webben: helsidesomdirigering.
  // iOS-appen: Google stoppar inloggning i inbäddade webbvyer, så Qid öppnas i
  // systemets inloggningsfönster (ASWebAuthenticationSession) och svaret kommer
  // tillbaka via se.qvenari.app://auth/callback. Apple fungerar likadant. Android: Chrome Custom
  // Tab och samma tillbakalänk (loginInCustomTab).
  loginWithQid: async (provider: QidProvider = 'google'): Promise<QidUser | null> => {
    if (!IS_CAPACITOR_BUNDLE) {
      await auth.login(provider);
      return null; // sidan lämnas; App.tsx tar emot ?code= efter omdirigeringen
    }
    const config = await auth.getConfig();
    const state = crypto.randomUUID();
    const authorizeUrl = buildAuthorizeUrl(config, NATIVE_REDIRECT_URI, state, provider);
    const url = Capacitor.getPlatform() === 'android'
      ? await loginInCustomTab(authorizeUrl)
      : (await QidAuth.start({ url: authorizeUrl, callbackScheme: NATIVE_CALLBACK_SCHEME })).url;
    const params = new URL(url).searchParams;
    if (params.get('state') !== state) throw new Error('Inloggningen kunde inte verifieras (state)');
    const code = params.get('code');
    if (!code) throw new Error(params.get('error') ?? 'Ingen inloggningskod från Qid');
    return exchangeCode(code, NATIVE_REDIRECT_URI);
  },

  // Exchange authorization code for tokens
  handleCallback: async (code: string, state: string): Promise<QidUser> => {
    const savedState = sessionStorage.getItem('oidc_state');
    if (savedState && savedState !== state) {
      throw new Error('State mismatch — possible CSRF');
    }
    sessionStorage.removeItem('oidc_state');

    const config = await auth.getConfig();
    return exchangeCode(code, config.redirect_uri);
  },

  // Direct login: web → BFF POST /auth/login på Qvenari-API (CORS); Capacitor → Qid /auth/login.
  directLogin: async (email: string, password: string): Promise<QidUser> => {
    const res = await fetch(resolveUrl('/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as any;
      throw new Error(err?.error ?? 'Inloggning misslyckades');
    }

    // Qid /auth/login och BFF /auth/direct-login: { token | access_token, user, expires_at }
    const data = await res.json() as { token?: string; access_token?: string; id_token?: string; user: QidUser; expires_at?: string };
    const tok = data.token ?? data.access_token ?? '';
    setTokens(tok, data.id_token ?? tok);
    // IK1: i appen går inloggningen direkt mot Qid – fråga Qvenari-API:t om kontot finns.
    const meRes = await fetch(resolveUrl('/auth/me'), { headers: bearerHeaders(tok) }).catch(() => null);
    if (meRes && !meRes.ok) {
      const err = await meRes.json().catch(() => ({})) as { error?: string; suspended?: boolean };
      if (isInviteRequiredBody(err)) { requestInviteCode(); throw new InviteRequiredError(err.error); }
      if (err.suspended) { clearTokens(); rememberSuspended(err.error ?? 'Kontot är avstängt'); throw new Error(err.error ?? 'Kontot är avstängt'); }
    }
    const user = meRes?.ok ? await meRes.json() as QidUser : data.user;
    auth.setUser(user);
    return user;
  },

  /**
   * IK1: lös in invitekoden (eller väntande inbjudningslänk till en mark) för den inloggade Qid-användaren.
   * Fel kod ger InviteRequiredError med API:ts felrad; för många försök ett vanligt Error.
   */
  redeemInvite: async (code?: string): Promise<QidUser> => {
    const res = await fetch(resolveUrl('/auth/invite'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders() },
      body: JSON.stringify({ code: code ?? '', inviteToken: pendingMarkInvite() ?? undefined }),
    });
    const data = await res.json().catch(() => ({})) as { error?: string; user?: QidUser };
    if (!res.ok) {
      if (isInviteRequiredBody(data)) throw new InviteRequiredError(data.error);
      throw new Error(data.error ?? 'Kunde inte kontrollera invitekoden');
    }
    const me = await auth.fetchMe();
    if (!me) throw new Error('Inloggningen har gått ut. Logga in igen.');
    return me;
  },

  // Logout
  logout: () => {
    clearTokens();
    window.location.href = '/';
  },

  // Fetch current user (validate token)
  fetchMe: async (): Promise<QidUser | null> => {
    const token = getAccessToken();
    if (!token) return null;
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const idToken = localStorage.getItem('qvenari_id_token');
      if (idToken) headers['X-Id-Token'] = idToken;
      const res = await fetch(resolveUrl('/auth/me'), { headers });
      if (!res.ok) {
        // AS1c: avstängt konto – logga ut och visa varför (ModerationNotice).
        if (res.status === 403) {
          const err = await res.json().catch(() => ({})) as { error?: string; suspended?: boolean };
          if (err.suspended) rememberSuspended(err.error ?? 'Kontot är avstängt');
          // IK1: inget Qvenari-konto än – behåll tokens, InviteCodeGate ber om koden.
          if (isInviteRequiredBody(err)) { localStorage.removeItem('qvenari_user'); requestInviteCode(); return null; }
        }
        clearTokens(); return null;
      }
      const user = await res.json() as QidUser;
      auth.setUser(user);
      return user;
    } catch {
      clearTokens();
      return null;
    }
  },
};

/**
 * Bearer-token plus id_token. API:t verifierar båda mot Qid och tar e-posten
 * från id_token när OIDC-access_token saknar den — skicka alltid med båda.
 */
export function bearerHeaders(token: string | null = getAccessToken()): Record<string, string> {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const idToken = localStorage.getItem('qvenari_id_token');
  if (idToken) headers['X-Id-Token'] = idToken;
  return headers;
}

/** API-nyckel, Bearer-token och id_token. */
export function authHeaders(): Record<string, string> {
  return { 'X-Api-Key': API_KEY, ...bearerHeaders() };
}

/**
 * Anropet som iOS-appen (BackgroundTrackPlugin) själv skickar jaktens spår med medan webbvyn
 * sover; pluginen lägger till `points` ([lat, lng, ms]) bland variablerna.
 */
export function huntTrackUpload(meetingId: string, avatarUrl?: string) {
  return {
    url: new URL(API_URL, window.location.href).toString(),
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    query: 'mutation ReportHuntTrack($meetingId: ID!, $points: [[Float!]!]!, $avatarUrl: String) { reportHuntTrack(meetingId: $meetingId, points: $points, avatarUrl: $avatarUrl) }',
    variables: { meetingId, avatarUrl: avatarUrl || null },
  };
}

/** POST till API:t med inloggningen; kastar med serverns felmeddelande. */
async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(resolveUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({})) as T & { error?: string; code?: string };
  if (!res.ok) throw Object.assign(new Error(data.error || 'Något gick fel'), { code: data.code, status: res.status });
  return data;
}

// J1: fält för planerade jakter och inbjudningar.
const PLANNED_HUNT_FIELDS = 'meetingTime meetingPlace meetingLat meetingLng message contactName contactPhone revision cancelledAt';
const INVITE_FIELDS = 'id eventId email name inviteType status comment respondedAt passLabel memberId phone emailedAt createdAt';
const CAL_EVENT_FIELDS = `id markId title description eventType startTime endTime location createdBy createdAt qmeetMeetingId status endedAt recordingUrl transcription summary observationsJson killsJson satPlanJson ${PLANNED_HUNT_FIELDS} invites { ${INVITE_FIELDS} token }`;
const HUNT_INVITATION_FIELDS = 'token status comment respondedAt name passLabel isGuest isHost hostName event { id markId title startTime endTime status satPlanJson meetingTime meetingPlace meetingLat meetingLng message contactName contactPhone revision cancelledAt } mark { id name imageUrl county lat lng } participants { name status passLabel isGuest isHost } gameRows { gameType familyLabel planned remaining status reason fee feeFrom }';

export async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...authHeaders() };

  const res = await fetch(API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json() as { data?: T; errors?: { message: string }[] };
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data as T;
}

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────

export type HunterApprovalStatus = 'none' | 'pending' | 'approved' | 'rejected';
export interface HunterApproval { status: HunterApprovalStatus; note: string | null; hasExam: boolean }
export interface HunterApprovalRequest {
  userId: string; email: string | null; name: string | null; status: HunterApprovalStatus;
  requestedAt: number | null; approvedAt: number | null; approvedBy: string | null; note: string | null;
  hunterExam: { imageUrl: string; holderName: string; examDate: string; examNumber: string; issuedBy: string } | null;
}

/** Åtelkamera: bilder mejlade till emailAddress hamnar i markens chatt, album och på kartnålen. */
export interface MarkCamera {
  id: string;
  markId: string;
  name: string;
  emailAddress: string;
  lat: number | null;
  lng: number | null;
  lastImageUrl: string | null;
  lastImageAt: string | null;
  createdAt: string;
}
const MARK_CAMERA_FIELDS = 'id markId name emailAddress lat lng lastImageUrl lastImageAt createdAt';

export interface Mark {
  id: string;
  name: string;
  /** AS1a: demomarken som alla utan egen mark ser (bara läsning). */
  isDemo?: boolean;
  description?: string;
  areaHa?: number;
  county?: string;
  municipality?: string;
  coordinatesJson?: string | null;
  address?: string;
  ownerUserId?: string;
  contactEmail?: string;
  contactPhone?: string;
  imageUrl?: string;
  galleryUrls?: string[];
  videoUrl?: string;
  pricePerYear?: number;
  gameTypesJson?: string;
  featured?: boolean;
  mapLat?: number | null;
  mapLng?: number | null;
  speciesOccurrenceJson?: string;
  facilitiesJson?: string;
  highlightsJson?: string;
  includesJson?: string;
  difficulty?: string;
  annualDrivenHunts?: number;
  afterhuntHuntersCount?: number;
  contactName?: string;
  swishNumber?: string | null;
  swishAmount?: number;
  ownerType?: string;
  ownerNumber?: string | null;
  isPublished?: boolean;
  qregistryId?: string | null;
  /** IM1: källan om marken är importerad (t.ex. 'minjaktlagsplats'). */
  importSource?: string | null;
  importSourceUrl?: string | null;
  importedAt?: string | null;
  costModel?: MarkCostModel | null;
  calendarEvents?: MarkCalendarEvent[];
  createdAt: string;
}

/** IM1: admin-togglen för importerade marker. */
export interface ImportedMarksStatus {
  showImported: boolean;
  hideInMinJakt: boolean;
  sources: { source: string; label: string; marks: number; published: number }[];
  updatedBy: string | null;
  updatedAt: string | null;
}
const IMPORTED_STATUS_FIELDS = 'showImported hideInMinJakt sources{source label marks published} updatedBy updatedAt';

/** GA3: en gallringsregel (API: services/retention.ts). */
export interface RetentionRule {
  key: string; label: string; description: string; why: string;
  basis: 'lag' | 'avtal' | 'berattigat_intresse'; action: 'delete' | 'anonymize' | 'notify';
  days: number; defaultDays: number; minDays: number; locked: boolean; enabled: boolean; mode: 'preview' | 'live';
  pending: number | null; pendingError: string | null; blockReason: string | null; tables: string[];
  lastRunAt: string | null; lastCount: number | null; lastStatus: string | null; updatedBy: string | null; updatedAt: string | null;
}
export interface RetentionStatus { disabled: boolean; maxRows: number; maxSharePct: number; rules: RetentionRule[] }
export interface RetentionLogEntry {
  id: string; ruleKey: string; label: string; trigger: 'nightly' | 'manual' | 'preview'; mode: 'preview' | 'live';
  status: 'ok' | 'blocked' | 'error' | 'disabled'; matched: number; affected: number; files: number; filesFailed: number;
  error: string | null; actor: string | null; startedAt: string; finishedAt: string | null;
}
export interface RetentionRunResult {
  ruleKey: string; mode: 'preview' | 'live'; status: RetentionLogEntry['status']; matched: number; affected: number;
  files: number; filesFailed: number; message: string | null;
}
const RETENTION_RULE_FIELDS = 'key label description why basis action days defaultDays minDays locked enabled mode pending pendingError blockReason tables lastRunAt lastCount lastStatus updatedBy updatedAt';

/** AD1: bokföring i Fortnox (API: src/bookkeeping/, graphql/admin/bookkeeping.ts). */
export interface BookkeepingAccounts { stripeBalance: number; ownerLiability: number; platformFeeRevenue: number; outputVat: number }
export interface BookkeepingStatus {
  configured: boolean; connected: boolean; companyName: string | null; voucherSeries: string;
  accounts: BookkeepingAccounts; defaultAccounts: BookkeepingAccounts; autoBookEnabled: boolean; autoBookLastDay: string | null;
  unbookedEvents: number; unbookedBackfilled: number; firstUnbookedDay: string | null; redirectUri: string | null;
  updatedBy: string | null; updatedAt: string | null;
}
export interface BookkeepingVoucherRow { account: number; debit: number; credit: number; description: string }
export interface BookkeepingDraft { day: string; category: string; description: string; eventCount: number; balanced: boolean; rows: BookkeepingVoucherRow[] }
export interface FortnoxVoucher {
  id: string; day: string; category: string; description: string; status: 'booking' | 'booked' | 'error';
  fortnoxSeries: string | null; fortnoxNumber: number | null; error: string | null; bookedBy: string | null;
  rows: BookkeepingVoucherRow[]; createdAt: string; updatedAt: string;
}
export interface LedgerEvent {
  id: string; day: string; kind: string; category: 'in' | 'transfer' | 'refund' | 'release'; occurredAt: string;
  markId: string | null; markName: string | null; referenceId: string; description: string;
  gross: number; fee: number; vat: number; owner: number; cash: number; backfilled: boolean; booked: boolean;
}
export interface BookkeepingPreview { drafts: BookkeepingDraft[]; vouchers: FortnoxVoucher[]; events: LedgerEvent[]; backfilledCount: number }
export interface BookkeepingLogEntry { id: string; voucherId: string; day: string; category: string; status: string; message: string | null; actor: string | null; createdAt: string }
const BOOKKEEPING_ACCOUNT_FIELDS = 'stripeBalance ownerLiability platformFeeRevenue outputVat';
const BOOKKEEPING_STATUS_FIELDS = `configured connected companyName voucherSeries accounts{${BOOKKEEPING_ACCOUNT_FIELDS}} defaultAccounts{${BOOKKEEPING_ACCOUNT_FIELDS}} autoBookEnabled autoBookLastDay unbookedEvents unbookedBackfilled firstUnbookedDay redirectUri updatedBy updatedAt`;
const VOUCHER_ROW_FIELDS = 'rows{account debit credit description}';
const FORTNOX_VOUCHER_FIELDS = `id day category description status fortnoxSeries fortnoxNumber error bookedBy ${VOUCHER_ROW_FIELDS} createdAt updatedAt`;

export interface FallAvgift { viltslag: string; kon: string; typ: string; kostnad: number; }
export interface KottKostnad { viltslag: string; kostnadPerKg: number; }
export interface MarkCostModel {
  markId: string;
  arsavgift: number;
  viltvardAvgiftPerAr: number;
  gastKostnadPerJakt: number;
  medlemKostnadPerJakt: number;
  fallavgifter: FallAvgift[];
  kottkostnad: KottKostnad[];
  kottIngar: boolean;
  antalDrevjakter: number;
  kanBokaKalendar: boolean;
  boterOlovligtVilt: number;
  boterEftersok: number;
  updatedAt: string;
}

export interface ArrendeSettings {
  speciesFallJson: string;
  subcatFallJson: string;
  meatKgJson: string;
  prisDrev: number | null;
  prisAlg: number | null;
  prisSmyg: number | null;
  prisAtel: number | null;
  prisFagel: number | null;
  updatedAt: string | null;
}

export interface Jaktlag {
  id: string;
  markId?: string;
  name: string;
  description?: string;
  season?: string;
  huntingRightsType?: string;
  maxMembers: number;
  recruiting: boolean;
  contactEmail?: string;
  createdAt: string;
}

export interface JaktlagMember {
  id: string;
  jaktlagId: string;
  userId?: string;
  name: string;
  email: string;
  role: string;
  hunterLicenseNo?: string;
  dogHandler: boolean;
  afterhuntCertified: boolean;
  status: string;
  invitedAt: string;
  joinedAt?: string;
}

export interface Jakt {
  id: string;
  jaktlagId: string;
  title: string;
  description?: string;
  gameTypes: string[];
  startTime: string;
  endTime?: string;
  meetingPoint?: string;
  status: string;
  createdBy: string;
  qregistryId?: string | null;
  createdAt: string;
}

export interface Post {
  id: string;
  jaktId: string;
  name: string;
  latitude?: number;
  longitude?: number;
  assignedMemberId?: string;
  assignedMember?: JaktlagMember;
  sector?: string;
  notes?: string;
}

export interface Observation {
  id: string;
  jaktId: string;
  memberId?: string;
  gameType: string;
  count: number;
  sex?: string;
  ageClass?: string;
  direction?: string;
  latitude?: number;
  longitude?: number;
  observedAt: string;
  notes?: string;
}

export interface Avskjutning {
  id: string;
  jaktId: string;
  memberId?: string;
  member?: JaktlagMember;
  gameType: string;
  sex?: string;
  ageClass?: string;
  actualWeightKg?: number;
  estimatedWeightKg?: number;
  trophyClass?: string;
  shotAt: string;
  fieldFee: number;
  kgPrice: number;
  totalMeatValue: number;
  qregistryObjectId?: string;
  notes?: string;
}

export interface Skjutmeny {
  id: string;
  jaktId: string;
  memberId: string;
  member?: JaktlagMember;
  gameType: string;
  maxCount: number;
  fieldFee: number;
  kgPrice: number;
  alreadyShot: number;
  remaining: number;
}

export interface AvskjutningsMal {
  id: string;
  jaktlagId: string;
  season: string;
  gameType: string;
  targetCount: number;
  currentCount: number;
  status: string;
  progressPercent: number;
}

export interface DashboardStats {
  totalMarks: number;
  totalJaktlag: number;
  activeJakter: number;
  totalKills: number;
  totalKillsThisSeason: number;
  goalProgress: { gameType: string; target: number; current: number; percent: number }[];
  recentObservations: Observation[];
  recentKills: Avskjutning[];
}

export interface LiveHuntStatus {
  jaktId: string;
  status: string;
  activePoster: number;
  totalObservations: number;
  totalKills: number;
  lastObservationAt?: string;
  participants: {
    postId: string;
    postName: string;
    memberName?: string;
    latitude?: number;
    longitude?: number;
    observationCount: number;
    killCount: number;
  }[];
}

export interface HuntListing {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  location: string;
  region: string;
  county?: string;
  huntType?: string | null;
  gameTypes: string[];
  primaryGameType: string;
  startDate: string;
  endDate?: string;
  imageUrl: string;
  price: number;
  priceUnit: string;
  spotsTotal: number;
  spotsAvailable: number;
  difficulty: string;
  includes: string[];
  highlights: string[];
  featured: boolean;
  status: string;
  contactName?: string;
  contactEmail?: string;
  speciesOccurrenceJson?: string;
  facilitiesJson?: string;
  annualDrivenHunts?: number;
  afterhuntHuntersCount?: number;
  mapLat?: number;
  mapLng?: number;
  galleryUrls?: string[];
  videoUrl?: string;
  swishNumber?: string;
  swishAmount?: number;
  markId?: string;
  createdAt: string;
}

export interface Booking {
  id: string;
  listingId: string;
  listing?: HuntListing;
  hunterName: string;
  hunterEmail: string;
  hunterPhone?: string;
  hunterLicenseNo?: string;
  status: string;
  contractSignedAt?: string;
  contractText?: string;
  amount?: number;
  swishNumber?: string;
  paymentStatus?: string;
  paymentConfirmedAt?: string;
  paymentReference?: string;
  signatureName?: string;
  notes?: string;
  createdAt: string;
}

export interface ScrapedTeam {
  id: string;
  name: string;
  county?: string;
  municipality?: string;
  areaHa?: number;
  memberCount?: number;
  contactEmail?: string;
  recruiting: boolean;
  gameTypes: string[];
  sourceUrl?: string;
  scrapedAt: string;
}

export interface MarkRolePermissions {
  role: string; label: string;
  canManageMembers: boolean; canEditMark: boolean; canManageCalendar: boolean;
  canManageShootingPlan: boolean; canChat: boolean; canViewMap: boolean;
  canEditMap: boolean; canStartHunt: boolean; canRecordKill: boolean;
  canManageMeatSharing: boolean; canPayFees: boolean; canViewAlbum: boolean; canUploadAlbum: boolean;
  canManageIntegrations: boolean; canInviteMembers: boolean; canRemoveMembers: boolean;
  canAssignRoles: boolean;
}

export interface MarkGroupPermissions {
  canManageCalendar: boolean; canStartHunt: boolean; canRecordKill: boolean;
  canChat: boolean; canInviteMembers: boolean; canManageMeatSharing: boolean;
  canPayFees: boolean; canViewMap: boolean; canEditMap: boolean;
  canManageShootingPlan: boolean; canViewAlbum: boolean; canUploadAlbum: boolean;
}

export interface MarkMember {
  id: string;
  markId: string;
  userId?: string;
  userEmail: string;
  name: string;
  role: string;
  avatarUrl?: string;
  phone?: string;
  dogHandler: boolean;
  joinedAt: string;
  status: string;
  permissions?: MarkRolePermissions;
}

export interface JaktklubSettings {
  isJaktklubb: boolean;
  maxJakttillfallen?: number | null;
  isPublished: boolean;
  publishedAt?: string | null;
  description?: string | null;
  kontaktEmail?: string | null;
  arsKostnad?: number | null;
  fallavgiftTyp?: string | null;
  fallavgiftKr?: number | null;
  kottPrisPerKg?: number | null;
  jagarKravJson?: string | null;
}

export interface JaktklubAd {
  groupId: string;
  markId: string;
  markName: string;
  groupName: string;
  groupColor: string;
  imageUrl?: string | null;
  memberCount: number;
  settings: JaktklubSettings;
  costModel?: MarkCostModel | null;
  requirements?: MarkRequirements | null;
  county?: string | null;
  municipality?: string | null;
  publishedAt: string;
}

export interface JaktklubApplication {
  id: string;
  groupId: string;
  markId: string;
  applicantEmail: string;
  applicantName: string;
  message?: string | null;
  status: string;
  createdAt: string;
  handledAt?: string | null;
  handledByEmail?: string | null;
}

export interface MarkGroup {
  id: string;
  markId: string;
  name: string;
  color: string;
  memberIds: string[];
  createdAt: string;
  permissions?: MarkGroupPermissions | null;
  jaktklubb?: JaktklubSettings | null;
}

export interface MarkOwnerContract {
  id: string;
  markId: string | null;
  registrantEmail: string;
  registrantName: string;
  fastighetsBeteckning: string | null;
  markName: string;
  areaHa: number;
  county: string | null;
  contactPhone: string | null;
  avskjutningsplanJson: string;
  calculatorJson: string;
  signatureName: string;
  signedAt: string;
  adminSignatureName: string | null;
  adminSignedAt: string | null;
  status: string;
  createdAt: string;
}

export interface CalendarEventInvite {
  id: string;
  eventId: string;
  email: string | null;
  name: string;
  inviteType: string;
  /** invited | yes | maybe | no */
  status: string;
  comment?: string | null;
  respondedAt?: string | null;
  passLabel?: string | null;
  memberId?: string | null;
  phone?: string | null;
  emailedAt?: string | null;
  /** Bara efter planHunt/updatePlannedHunt (till jaktledaren). */
  token?: string | null;
  createdAt: string;
}

export interface MarkInviteLink {
  id: string;
  markId: string;
  token: string;
  label?: string;
  role: string;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  maxUses: number;
  useCount: number;
  revokedAt?: string;
  isValid: boolean;
  /** ME1: gruppen som den som löser in länken läggs i. */
  groupId?: string | null;
  groupName?: string | null;
}

export interface InviteLinkPreview {
  markId: string;
  markName: string;
  role: string;
  isValid: boolean;
  alreadyMember?: boolean;
}

export interface RedeemInviteResult {
  markId: string;
  markName: string;
  role: string;
  alreadyMember: boolean;
}

/** Bild eller video från en jakt (se src/huntMedia.ts). takenAt i millisekunder. */
export interface HuntMedia {
  id: string;
  markId: string;
  eventId: string | null;
  url: string;
  mediaType: 'image' | 'video';
  takenAt: number;
  lat: number | null;
  lng: number | null;
  positionSource: 'photo' | 'track' | null;
  sourceId: string | null;
  createdAt: string;
}

export type HuntMediaInput = Omit<HuntMedia, 'id' | 'markId' | 'eventId' | 'createdAt' | 'lat' | 'lng' | 'positionSource' | 'sourceId'> & {
  lat?: number; lng?: number; positionSource?: 'photo' | 'track'; sourceId?: string;
};

const HUNT_MEDIA_FIELDS = 'id markId eventId url mediaType takenAt lat lng positionSource sourceId createdAt';

/** Pågående jakt på en jaktmark (Qmeet-möte). Tider i millisekunder. */
export interface MarkHunt { meetingId: string; markId: string; title: string; startedBy: string; startedAt: number; endedAt: number | null }
/** livekitToken saknas när man ansluter utan radio (Apple Watch). */
export interface MarkHuntSession { hunt: MarkHunt; livekitToken: string | null; livekitUrl: string | null; identity: string | null }
/** Deltagares senaste position på servern — t.ex. från Apple Watch, som inte når LiveKit. */
export interface HuntPosition {
  userEmail: string; name: string; avatarUrl: string | null; lat: number; lng: number;
  accuracy: number | null; heading: number | null; source: 'app' | 'watch'; updatedAt: number; isMe: boolean;
}
/** Hund med en Apple Watch i hundläge. barkingAt = senaste skallet (ms). */
export interface HuntDogPosition {
  dogId: string; dogName: string; ownerEmail: string; ownerName: string; lat: number; lng: number;
  accuracy: number | null; battery: number | null; barkingAt: number | null; updatedAt: number;
  /** Din hund — då kan du ändra klockans inställningar. */
  isMine: boolean;
  /** hunt = var 10:e s, saver = energispar, var 30:e s */
  powerMode: 'hunt' | 'saver';
  mic: boolean;
}

/** KA1: deltagarens status i jakten (telefon + klocka). null = enheten skickar inte värdet. */
export interface HuntParticipantStatus {
  userEmail: string; identity: string | null;
  /** 0–1 */ battery: number | null; charging: boolean | null;
  steps: number | null; heartRate: number | null;
  /** m/s */ speed: number | null; /** meter över havet */ altitude: number | null;
  speakerOn: boolean | null; sources: string[]; updatedAt: number; isMe: boolean;
}
export type HuntStatusInput = Partial<Pick<HuntParticipantStatus, 'identity' | 'battery' | 'charging' | 'steps' | 'heartRate' | 'speed' | 'altitude' | 'speakerOn'>>;

export interface HuntTrack { kind: 'hunter' | 'dog'; id: string; name: string; isMe: boolean; points: [number, number, number][] }
export interface DogWatch { dogId: string; name: string; powerMode: 'hunt' | 'saver'; mic: boolean }

export interface HuntChatMessage {
  id: string; clientId: string; senderEmail: string; senderName: string; text: string | null; imageUrl: string | null;
  /** I2: röst- och videomeddelanden (src/q/huntListen.ts sendHuntChatMedia). */
  audioUrl?: string | null; videoUrl?: string | null; durationMs?: number | null;
  lat: number | null; lng: number | null; source: 'app' | 'watch'; createdAt: number; isMe: boolean;
}

const MARK_HUNT_FIELDS = 'meetingId markId title startedBy startedAt endedAt';
const HUNT_SESSION_FIELDS = `hunt { ${MARK_HUNT_FIELDS} } livekitToken livekitUrl identity`;
const HUNT_CHAT_FIELDS = 'id clientId senderEmail senderName text imageUrl audioUrl videoUrl durationMs lat lng source createdAt isMe';

/** Kalenderns bokningsläge (API: lib/bookingRules.ts). */
export type BookingMode = 'open' | 'closed';
export interface MarkBookingPeriod { id: string; kind: 'blocked' | 'bookable'; start: string; end: string; note: string | null }

export interface MarkCalendarEvent {
  id: string;
  markId: string;
  title: string;
  description?: string;
  eventType: string;
  startTime: string;
  endTime?: string;
  location?: string;
  createdBy?: string;
  createdAt: string;
  qmeetMeetingId?: string;
  /** Bara i webben: jakten (mark_hunts) som rutan efter jakten visar, så att fällt vilt hamnar i rätt rapport. */
  huntMeetingId?: string;
  status: string;
  endedAt?: string;
  recordingUrl?: string;
  transcription?: string;
  summary?: string;
  observationsJson?: string;
  killsJson?: string;
  satPlanJson?: string;
  invites: CalendarEventInvite[];
  // J1 · Planera jakt
  meetingTime?: string | null;
  meetingPlace?: string | null;
  meetingLat?: number | null;
  meetingLng?: number | null;
  message?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  revision?: number | null;
  cancelledAt?: string | null;
}

/** Inbjudan till en planerad jakt så som den inbjudne ser den (J1). */
export interface HuntInvitation {
  token: string | null;
  status: string | null;
  comment: string | null;
  respondedAt: string | null;
  name: string | null;
  passLabel: string | null;
  isGuest: boolean;
  isHost: boolean;
  hostName: string;
  event: {
    id: string; markId: string; title: string; startTime: string; endTime: string | null; status: string; satPlanJson: string | null;
    meetingTime: string | null; meetingPlace: string | null; meetingLat: number | null; meetingLng: number | null;
    message: string | null; contactName: string | null; contactPhone: string | null; revision: number | null; cancelledAt: string | null;
  };
  mark: { id: string; name: string; imageUrl: string | null; county: string | null; lat: number | null; lng: number | null };
  participants: { name: string; status: string; passLabel: string | null; isGuest: boolean; isHost: boolean }[];
  /** Skjutmenyn för jaktdagen; fällavgiften gäller den som tittar. */
  gameRows: HuntGameRowWithFee[];
}

export interface HuntGameRowWithFee {
  gameType: string; familyLabel: string; planned: number | null; remaining: number | null;
  status: GameStatus; reason?: string | null; fee: number | null; feeFrom: boolean;
}

/** Skjutmenyns status per viltslag (API: lib/skjutmeny.ts). lovligt = grön, fel_tid = måne, slut = röd. */
export type GameStatus = 'lovligt' | 'fel_tid' | 'slut' | 'okand' | 'ej_jakttid';

export interface SkjutmenyRow {
  gameType: string; familyKey: string; familyLabel: string;
  planned: number | null; remaining: number | null; actual: number | null;
  status: GameStatus; reason: string; notes: string[]; periods: string[];
  fieldFee: number | null; feeFrom: boolean; kgPrice: number | null;
}

export interface MarkSkjutmeny {
  markId: string; at: string; season: string; county: string | null;
  sunrise: string | null; sunset: string | null; sunNote: string | null; kottIngar: boolean;
  source: { title: string; url: string; amendedThrough: string | null; validTo: string | null } | null;
  rows: SkjutmenyRow[];
  /** SM2: fågelsektionen – alla fåglar med jakttid i länet; rows tom när fågel är avstängd. */
  birds: { enabled: boolean; canToggle: boolean; rows: SkjutmenyRow[] };
}

/** Viltslag med jakttid i markens län någon gång under året ∩ viltvårdsplanen (annonsen). */
export interface AllowedGame { familyKey: string; label: string; state: 'open' | 'unknown'; openToday: boolean }

export interface PlanHuntInput {
  title: string;
  startTime: string;
  endTime?: string | null;
  meetingTime: string;
  meetingPlace?: string | null;
  meetingLat?: number | null;
  meetingLng?: number | null;
  message?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  satPlanJson?: string | null;
  gameRowsJson?: string | null;
  recipients: { memberId?: string | null; email?: string | null; phone?: string | null; name?: string | null; passLabel?: string | null }[];
}

export interface SatTemplate { id: string; name: string; planJson: string; createdAt: string }

export interface MarkChatMessage {
  id: string;
  markId: string;
  senderEmail: string;
  senderName: string;
  message?: string;
  imageUrl?: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  messageType: string;
  createdAt: string;
}

/** CH1: en rad i chattlistan; key = "mark:<markId>". */
export interface ChatSummary {
  key: string;
  kind: string;
  markId: string | null;
  title: string;
  imageUrl: string | null;
  lastMessage: string | null;
  lastSenderName: string | null;
  lastSenderEmail: string | null;
  lastAt: string | null;
  unreadCount: number;
}

export interface MarkShootingPlanItem {
  id: string;
  markId: string;
  season: string;
  gameType: string;
  plannedCount: number;
  actualCount: number;
  costPerAnimal?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MarkMeatSharingItem {
  id: string;
  markId: string;
  season: string;
  gameType: string;
  animalNumber: number;
  shotByEmail?: string;
  shotByName?: string;
  shotDate?: string;
  weightKg?: number;
  sharedToEmail: string;
  sharedToName: string;
  portionKg?: number;
  notes?: string;
  createdAt: string;
}

export interface MarkMeatQueueItem {
  id: string;
  markId: string;
  gameType: string;
  memberEmail: string;
  memberName: string;
  queuePosition: number;
  lastReceivedAt?: string;
}

export interface ViltHandlingLog {
  id: string;
  viltRegId: string;
  oldStatus?: string;
  newStatus: string;
  changedByEmail?: string;
  changedByName?: string;
  comment?: string;
  createdAt: string;
}

export type ViltHandlingStatus = 'påskjutet' | 'död' | 'upphämtad' | 'urtagen' | 'avpälsad' | 'hängd' | 'styckad';

export interface MarkViltRegistration {
  id: string;
  markId: string;
  season: string;
  gameType: string;
  animalNumber: number;
  djurId?: string;
  handlingStatus: ViltHandlingStatus;
  falltViltPinId?: string;
  shotDate?: string;
  shotByEmail?: string;
  shotByName?: string;
  sex?: string;
  ageClass?: string;
  estimatedWeightKg?: number;
  disposition: 'pending' | 'to_list' | 'to_shooter' | 'shooter_portion' | 'to_landowner' | 'for_sale';
  shareCount: number;
  avpalsat: boolean;
  slaktviktKg?: number;
  trikintestad: boolean;
  cesiumtestad: boolean;
  saleNotes?: string;
  fallavgiftKr?: number;
  kottkostnadPerKg?: number;
  viktKg?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/** I2: albumet kan innehålla videor (videomeddelanden från jaktchatten). imageUrl är då videons adress. */
export function isVideoUrl(url: string | null | undefined): boolean {
  return !!url && /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(url);
}

export interface MarkAlbumImage {
  id: string;
  markId: string;
  imageUrl: string;
  caption?: string;
  sourceType: string;
  sourceMessageId?: string;
  uploadedBy?: string;
  isPublic: boolean;
  createdAt: string;
}

export interface UserEquipment {
  id: string;
  userId: string;
  name: string;
  category: string;
  description?: string;
  imageUrl?: string;
  condition?: string;
  forRent: boolean;
  rentPrice: number;
  forSale: boolean;
  salePrice: number;
  qregistryObjectId?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserWeapon {
  id: string;
  userId: string;
  name: string;
  weaponType: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  caliber?: string;
  licenseNumber?: string;
  licenseType?: string;
  licenseHolder?: string;
  licenseExpires?: string;
  licenseImageUrl?: string;
  licenseImageUrlBack?: string;
  description?: string;
  imageUrl?: string;
  condition?: string;
  forRent: boolean;
  rentPrice: number;
  forSale: boolean;
  salePrice: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  licenses?: WeaponLicense[];
}

export interface WeaponLicense {
  id: string;
  weaponId: string;
  partType: string;
  serialNumber?: string;
  licenseNumber?: string;
  licenseType?: string;
  licenseHolder?: string;
  licenseExpires?: string;
  licenseImageUrl?: string;
  licenseImageUrlBack?: string;
  caliber?: string;
  notes?: string;
  createdAt: string;
}

export interface WeaponLoan {
  id: string;
  weaponId: string;
  lenderUserId: string;
  borrowerName?: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  borrowerPhoneMobile?: string;
  borrowerAddress?: string;
  borrowerPostalCode?: string;
  borrowerCity?: string;
  borrowerPersonnummer?: string;
  purpose?: string;
  loanStart?: string;
  loanEnd?: string;
  notes?: string;
  status: string;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────
// API CALLS
// ─────────────────────────────────────────────────────────────────

// ── Priser per grupp och annonser (API: lib/markAds.ts) ──────────
export interface EffectivePrice {
  arsavgift: number;
  prisPerJakt: number;
  fallavgifter: FallAvgift[];
  kottkostnad: KottKostnad[];
  kottIngar: boolean;
}

/** Gruppens egna priser; null = ärver markens grundpris. `effective` är det som gäller. */
export interface MarkGroupPrice {
  groupId: string;
  markId: string;
  groupName: string;
  groupColor: string;
  isJaktklubb: boolean;
  memberCount: number;
  arsavgift: number | null;
  prisPerJakt: number | null;
  fallavgifter: FallAvgift[] | null;
  kottkostnad: KottKostnad[] | null;
  kottIngar: boolean | null;
  effective: EffectivePrice;
}

export type MarkGroupPriceInput = Partial<Pick<MarkGroupPrice, 'arsavgift' | 'prisPerJakt' | 'fallavgifter' | 'kottkostnad' | 'kottIngar'>>;

export type AdKind = 'jaktlag' | 'jaktklubb' | 'enskild' | 'jaktpaket';
export const AD_KIND_LABEL: Record<AdKind, string> = { jaktlag: 'Jaktlag', jaktklubb: 'Jaktklubb', enskild: 'Enskild jakt', jaktpaket: 'Jaktpaket' };

export interface AdDate { start: string; end: string }
export interface AdItem { markId: string; groupId: string | null; kind: 'jaktdagar' | 'jaktklubb'; days: number }
export interface AdLine { markId: string; markName: string; groupId: string | null; groupName: string | null; kind: 'jaktdagar' | 'jaktklubb'; days: number; amount: number; price: EffectivePrice }

/** Annons skapad från markens data — pris och markuppgifter räknas fram av API:t. */
export interface MarkAd {
  id: string;
  kind: AdKind;
  markId: string;
  markName: string;
  county: string | null;
  municipality: string | null;
  groupId: string | null;
  groupName: string | null;
  title: string;
  description: string | null;
  imageUrl: string | null;
  dates: AdDate[];
  days: number;
  spots: number | null;
  status: 'draft' | 'published';
  memberCount: number | null;
  priceAmount: number;
  priceUnit: 'år' | 'dag' | 'paket';
  price: EffectivePrice | null;
  lines: AdLine[];
  mark: Mark | null;
  canEdit: boolean;
  publishedAt: string | null;
  /** Boende som kan köpas tillsammans med jakten (L1). */
  lodgingId: string | null;
  lodging: MarkLodging | null;
  /** S1: såterna en enskild jakt gäller (tomt = hela marken). */
  satIds?: string[];
  satNames?: string[];
}

/** S1: något som upptar markens såter en dag (planerad/pågående jakt eller köpt annons). */
export interface SatOccupancy {
  key: string;
  kind: 'hunt' | 'ad';
  title: string;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  status: string | null;
  satIds: string[];
  wholeMark: boolean;
}

export interface MarkAdInput {
  id?: string | null;
  kind: AdKind;
  markId: string;
  groupId?: string | null;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  dates?: AdDate[];
  spots?: number | null;
  items?: AdItem[];
  lodgingId?: string | null;
  satIds?: string[];
}

/** Boende på en jaktmark (L1). Priser i kronor. */
export type LodgingPriceUnit = 'lodging' | 'person';
export type LodgingExtraUnit = 'person' | 'booking';
export interface MarkLodging {
  id: string; markId: string; name: string; description: string | null;
  pricePerNight: number; priceUnit: LodgingPriceUnit; capacity: number; rooms: number | null;
  linensIncluded: boolean; linensPrice: number | null; linensUnit: LodgingExtraUnit;
  cleaningIncluded: boolean; cleaningPrice: number | null; cleaningUnit: LodgingExtraUnit;
  checkInTime: string | null; checkOutTime: string | null; cancellationPolicy: string | null;
  active: boolean; bookable: boolean; bookBlockedReason: string | null;
  /** EK2: bilder och instruktioner (instruktionerna bara för medlemmar och den som bokat, annars null). */
  images: string[]; checkInInfo: string | null; checkOutInfo: string | null; cleaningInfo: string | null;
}
export type MarkLodgingInput = Omit<MarkLodging, 'id' | 'bookable' | 'bookBlockedReason'> & { id?: string | null };
export interface LodgingNight { date: string; bedsFree: number; bookable: boolean }
export interface LodgingLine { name: string; quantity: number; unitPrice: number; amount: number }
export interface LodgingQuote {
  checkIn: string; checkOut: string; nights: number; guests: number; linens: boolean; cleaning: boolean;
  lines: LodgingLine[]; amount: number; platformFee: number; available: boolean; unavailableReason: string | null;
}
/** Vistelse som skickas med ett köp (fristående eller tillsammans med en annons). */
export interface LodgingStay { lodgingId: string; checkIn: string; checkOut: string; guests: number; linens: boolean; cleaning: boolean }
export type LodgingBookingStatus = 'checkout' | 'paid' | 'reserved' | 'rejected' | 'released' | 'cancelled';
export interface LodgingBooking {
  id: string; lodgingId: string; lodgingName: string | null; markId: string; markName: string | null;
  userId: string; userEmail: string; userName: string | null; checkIn: string; checkOut: string; nights: number; guests: number;
  linens: boolean; cleaning: boolean; lines: LodgingLine[]; amount: number; platformFee: number; currency: string;
  status: LodgingBookingStatus; adPurchaseId: string | null; adTitle: string | null; paidAt: string | null; createdAt: string;
  checkInTime?: string | null; checkOutTime?: string | null; checkInInfo?: string | null; checkOutInfo?: string | null; cleaningInfo?: string | null;
}
const LODGING_FIELDS = 'id markId name description pricePerNight priceUnit capacity rooms linensIncluded linensPrice linensUnit cleaningIncluded cleaningPrice cleaningUnit checkInTime checkOutTime cancellationPolicy active bookable bookBlockedReason images checkInInfo checkOutInfo cleaningInfo';
const LODGING_LINE_FIELDS = 'name quantity unitPrice amount';
const LODGING_BOOKING_FIELDS = `id lodgingId lodgingName markId markName userId userEmail userName checkIn checkOut nights guests linens cleaning lines{${LODGING_LINE_FIELDS}} amount platformFee currency status adPurchaseId adTitle paidAt createdAt checkInTime checkOutTime checkInInfo checkOutInfo cleaningInfo`;

export interface EditableMark { id: string; name: string; groups: MarkGroup[] }

/** Köp av en annons (P1). Kortet reserveras och dras när marken godkänner jägaren. */
export type AdPurchaseStatus = 'checkout' | 'reserved' | 'approving' | 'approved' | 'rejected' | 'released_timeout' | 'cancelled' | 'capture_failed';
export interface AdPurchase {
  id: string; adId: string; adTitle: string | null; adKind: AdKind; markId: string; markName: string | null; groupId: string | null;
  userId: string; userEmail: string; userName: string | null; dateStart: string | null; dateEnd: string | null;
  amount: number; platformFee: number; currency: string; status: AdPurchaseStatus;
  reservedAt: string | null; releaseAt: string | null; decidedAt: string | null; decisionNote: string | null; createdAt: string;
  /** Boende i samma betalning (L1); beloppet ingår i amount. */
  lodgingBookingId: string | null;
}
export interface AdPurchaseQuote { amount: number; platformFee: number; dateStart: string | null; dateEnd: string | null; description: string; spotsLeft: number | null }
/** applyWithoutCard (D5): jaktlag och gratis grupper söks utan kort – applyToAd i stället för Stripe. */
export interface AdBuyInfo { buyable: boolean; buyBlockedReason: string | null; spotsLeft: number | null; applyWithoutCard: boolean }
export interface StripeConnectStatus { connected: boolean; chargesEnabled: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean; needsReconnect?: boolean }
const PURCHASE_FIELDS = 'id adId adTitle adKind markId markName groupId userId userEmail userName dateStart dateEnd amount platformFee currency status reservedAt releaseAt decidedAt decisionNote lodgingBookingId createdAt';
const CONNECT_FIELDS = 'connected chargesEnabled payoutsEnabled detailsSubmitted needsReconnect';

// ── P2/P3/KO1/KO2: antagning, avtal och signering ──
export type AdmissionStage = 'applied' | 'interview' | 'approved' | 'rejected' | 'expired' | 'contract_sent' | 'signed' | 'member' | 'ended';
export interface AdmissionRequirement { key: string; label: string; status: 'ok' | 'missing' | 'manual'; detail: string | null }
export interface AdmissionProfile {
  name: string; email: string; avatarUrl: string | null; bio: string | null; region: string | null; experienceLevel: string | null;
  hunterSinceYear: number | null; dogHandler: boolean; dogs: number; afterhuntCertified: boolean; hasExam: boolean; platformApproval: string | null;
  validWeaponLicenses: number; weaponTypes: string[]; shootingTest: string | null; insurance: string | null;
}
export interface SigningRequestInfo {
  id: string; title: string; purpose: string; status: 'sent' | 'signed' | 'cancelled'; signerEmail: string; signerName: string | null;
  sentAt: string; signedAt: string | null; signedName: string | null; method: string | null; signedPdfSha256: string | null;
}
export interface MarkAdmission {
  id: string; markId: string; markName: string | null; userId: string; userEmail: string; userName: string | null;
  stage: AdmissionStage; stageLabel: string; adId: string | null; adTitle: string | null; adKind: AdKind | null;
  dateStart: string | null; dateEnd: string | null; amount: number | null; purchaseStatus: AdPurchaseStatus | null;
  /** ms: reservationen släpps då om ingen beslutat (D1, ~7 dygn hos Stripe) */
  releaseAt: string | null; decisionNote: string | null; groupId: string | null; groupName: string | null;
  interviewAt: string | null; interviewNote: string | null; validUntil: string | null; lastError: string | null; createdAt: string; updatedAt: string;
  profile: AdmissionProfile | null; requirements: AdmissionRequirement[] | null; meetsRequirements: boolean | null;
  contract: SigningRequestInfo | null; signingToken: string | null;
}
export interface AdmissionProfileFields {
  hunter_since_year: number | null; dog_handler: boolean; afterhunt_certified: boolean;
  shooting_test: { kind?: string | null; validUntil?: string | null } | null;
  insurance: { provider?: string | null; validUntil?: string | null } | null;
}
export interface InterviewSession { title: string; livekitToken: string; livekitUrl: string | null; identity: string }
export interface SigningDocument {
  id: string; scope: 'mark' | 'platform'; markId: string | null; groupId: string | null; title: string; bodyText: string | null;
  fileName: string | null; fileMime: string | null; fileSha256: string | null; kind: 'template' | 'file'; active: boolean; createdAt: string;
}
export interface PlatformAgreementStatus { document: SigningDocument | null; request: SigningRequestInfo | null; signingToken: string | null }
/** Det den som öppnar /avtal/<token> ser (GET /api/signing/:token). */
export interface SigningView {
  title: string; purpose: 'admission' | 'platform'; markName: string | null; signerName: string | null; signerEmail: string;
  status: 'sent' | 'signed' | 'cancelled'; bodyText: string | null;
  file: { name: string; mime: string; text: string | null; sha256: string } | null;
  signedAt: number | null; signedName: string | null; methods: { id: string; label: string; available: boolean }[];
}
const SIGNING_REQ_FIELDS = 'id title purpose status signerEmail signerName sentAt signedAt signedName method signedPdfSha256';
const ADMISSION_FIELDS = `id markId markName userId userEmail userName stage stageLabel adId adTitle adKind dateStart dateEnd amount purchaseStatus releaseAt decisionNote groupId groupName interviewAt interviewNote validUntil lastError createdAt updatedAt signingToken contract{${SIGNING_REQ_FIELDS}}`;
const ADMISSION_DETAIL_FIELDS = `${ADMISSION_FIELDS} meetsRequirements requirements{key label status detail} profile{name email avatarUrl bio region experienceLevel hunterSinceYear dogHandler dogs afterhuntCertified hasExam platformApproval validWeaponLicenses weaponTypes shootingTest insurance}`;
const SIGNING_DOC_FIELDS = 'id scope markId groupId title bodyText fileName fileMime fileSha256 kind active createdAt';
const PLATFORM_AGREEMENT_FIELDS = `document{${SIGNING_DOC_FIELDS}} request{${SIGNING_REQ_FIELDS}} signingToken`;

async function signingJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(resolveUrl(path), init);
  const data = await res.json().catch(() => ({})) as T & { error?: string; code?: string };
  if (!res.ok) throw Object.assign(new Error(data.error || 'Något gick fel'), { code: data.code, status: res.status });
  return data;
}

/** Samma markfält som marknadens lista (api.marks). */
const MARK_FIELDS_FOR_ADS = `id name description areaHa county municipality address contactEmail contactPhone contactName imageUrl galleryUrls videoUrl pricePerYear gameTypesJson featured mapLat mapLng speciesOccurrenceJson facilitiesJson highlightsJson includesJson difficulty annualDrivenHunts afterhuntHuntersCount isPublished qregistryId importSource importSourceUrl importedAt costModel { markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt fallavgifter { viltslag kon typ kostnad } kottkostnad { viltslag kostnadPerKg } kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt } calendarEvents { id markId title description eventType startTime endTime location createdBy createdAt status invites { id email name inviteType status } } createdAt`;
const EFFECTIVE_FIELDS = 'arsavgift prisPerJakt fallavgifter{viltslag kon typ kostnad} kottkostnad{viltslag kostnadPerKg} kottIngar';
const GROUP_PRICE_FIELDS = `groupId markId groupName groupColor isJaktklubb memberCount arsavgift prisPerJakt fallavgifter{viltslag kon typ kostnad} kottkostnad{viltslag kostnadPerKg} kottIngar effective{${EFFECTIVE_FIELDS}}`;
const AD_FIELDS = `id kind markId markName county municipality groupId groupName title description imageUrl dates{start end} days spots status memberCount priceAmount priceUnit price{${EFFECTIVE_FIELDS}} lines{markId markName groupId groupName kind days amount price{${EFFECTIVE_FIELDS}}} canEdit publishedAt lodgingId lodging{${LODGING_FIELDS}} satIds satNames`;

/** Pushnotiser: av/på per typ och tysta timmar (TT:MM). devices = antal telefoner som tar emot. */
export interface PushSettings {
  chat: boolean; album: boolean; hunt: boolean; invite: boolean; purchase: boolean;
  quietStart: string | null; quietEnd: string | null; devices: number;
}
const PUSH_SETTINGS_FIELDS = 'chat album hunt invite purchase quietStart quietEnd devices';

// ─────────────────────────────────────────────────────────────────
// GU1 · Gäst i jakten utan inloggning (API: routes/guestJoin.ts)
// ─────────────────────────────────────────────────────────────────

/** Insläppt gäst: jaktens anrop (positioner, chatt, spår) går då till gäst-API:t med gästtokenen. */
let guestHunt: { meetingId: string; token: string } | null = null;
export function setGuestHunt(s: { meetingId: string; token: string } | null): void { guestHunt = s; }
const guestFor = (meetingId: string) => (guestHunt && guestHunt.meetingId === meetingId ? guestHunt : null);
/** Är den här jakten öppnad som gäst (inte inloggad deltagare)? */
export const isGuestHunt = (meetingId: string) => !!guestFor(meetingId);

export class GuestApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

async function guestCall<T>(path: string, init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
  const res = await fetch(resolveUrl(`/api/guest${path}`), {
    method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
    headers: { ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }), ...init.headers },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const json = await res.json().catch(() => ({})) as T & { error?: string };
  if (!res.ok) throw new GuestApiError(json.error || `HTTP ${res.status}`, res.status);
  return json;
}
/** Gästtoken (GU1) för anrop där en insläppt jaktgäst får läsa, t.ex. kartnålarna. */
export const tokenHeader = (): Record<string, string> => (guestHunt ? { 'X-Guest-Token': guestHunt.token } : {});

export type GuestStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'left' | 'ended';
export interface GuestApproved {
  status: 'approved'; name: string; guestToken: string; tokenExpiresAt: number;
  hunt: { meetingId: string; markId: string; title: string };
  mark: { id: string; name: string; municipality?: string | null; mapLat?: number | null; mapLng?: number | null; coordinatesJson?: string | null };
}
export interface GuestListItem { id: string; name: string; status: 'pending' | 'approved'; createdAt: number; expiresAt: number }

export const guestApi = {
  /** Gästen: in i väntrummet med jaktens kod eller länkens mötes-id. */
  request: (by: { code?: string; meetingId?: string }, name: string) =>
    guestCall<{ requestId: string; secret: string; expiresAt: number }>('/requests', { body: { ...by, name } }),
  status: (id: string, secret: string) =>
    guestCall<{ status: Exclude<GuestStatus, 'approved'>; expiresAt?: number } | GuestApproved>(`/requests/${encodeURIComponent(id)}`, { headers: { 'X-Guest-Secret': secret } }),
  cancel: (id: string, secret: string) =>
    guestCall<{ ok: boolean }>(`/requests/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {}, headers: { 'X-Guest-Secret': secret } }),
  voice: () => guestCall<{ livekitToken: string; livekitUrl: string | null; identity: string; title: string }>('/voice', { method: 'POST', body: {}, headers: tokenHeader() }),
  hunt: () => guestCall<{ open: boolean; meetingId: string; markId: string; title: string }>('/hunt', { headers: tokenHeader() }),
  /** RQ2: gästen pratar i radion – håller den igång (gästen saknar inloggning, så /api/q/hunt går inte). */
  radioActivity: () => guestCall<{ radioStartedAt: number | null }>('/hunt/radio/activity', { method: 'POST', body: {}, headers: tokenHeader() }),

  /** Deltagare: jaktens anslutningskod och gästerna som väntar eller är insläppta. */
  invite: (meetingId: string) =>
    guestCall<{ code: string; formatted: string }>(`/hunts/${encodeURIComponent(meetingId)}/invite`, { headers: bearerHeaders() }),
  list: (meetingId: string) =>
    guestCall<GuestListItem[]>(`/hunts/${encodeURIComponent(meetingId)}/requests`, { headers: bearerHeaders() }),
  decide: (meetingId: string, id: string, approve: boolean) =>
    guestCall<{ status: string }>(`/hunts/${encodeURIComponent(meetingId)}/requests/${encodeURIComponent(id)}`, { body: { approve }, headers: bearerHeaders() }),
};

export const api = {
  // ── Priser per grupp och annonser ──
  markGroupPrices: (markId: string) =>
    gql<{ markGroupPrices: MarkGroupPrice[] }>(`query($markId:ID!){markGroupPrices(markId:$markId){${GROUP_PRICE_FIELDS}}}`, { markId }).then(d => d.markGroupPrices),
  upsertMarkGroupPrice: (markId: string, groupId: string, input: MarkGroupPriceInput) =>
    gql<{ upsertMarkGroupPrice: MarkGroupPrice }>(`mutation($markId:ID!,$groupId:ID!,$input:MarkGroupPriceInput!){upsertMarkGroupPrice(markId:$markId,groupId:$groupId,input:$input){${GROUP_PRICE_FIELDS}}}`, { markId, groupId, input }).then(d => d.upsertMarkGroupPrice),
  markAds: (markId: string) =>
    gql<{ markAds: MarkAd[] }>(`query($markId:ID!){markAds(markId:$markId){${AD_FIELDS}}}`, { markId }).then(d => d.markAds),
  /** Publicerade annonser med markens uppgifter (för marknadens listor och jaktsidan). */
  demoMarkHidden: () =>
    gql<{ demoMarkHidden: boolean }>(`query{demoMarkHidden}`).then(d => d.demoMarkHidden),
  setDemoMarkHidden: (hidden: boolean) =>
    gql<{ setDemoMarkHidden: boolean }>(`mutation($hidden:Boolean!){setDemoMarkHidden(hidden:$hidden)}`, { hidden }).then(d => d.setDemoMarkHidden),
  importedMarksStatus: () =>
    gql<{ importedMarksStatus: ImportedMarksStatus }>(`query{importedMarksStatus{${IMPORTED_STATUS_FIELDS}}}`).then(d => d.importedMarksStatus),
  setHideImportedInMinJakt: (hide: boolean) =>
    gql<{ setHideImportedInMinJakt: ImportedMarksStatus }>(`mutation($hide:Boolean!){setHideImportedInMinJakt(hide:$hide){${IMPORTED_STATUS_FIELDS}}}`, { hide }).then(d => d.setHideImportedInMinJakt),
  setShowImportedMarks: (show: boolean) =>
    gql<{ setShowImportedMarks: ImportedMarksStatus }>(`mutation($show:Boolean!){setShowImportedMarks(show:$show){${IMPORTED_STATUS_FIELDS}}}`, { show }).then(d => d.setShowImportedMarks),
  // GA3: GDPR-gallring (bara plattformsadmin; servern kontrollerar).
  retentionStatus: () =>
    gql<{ retentionStatus: RetentionStatus }>(`query{retentionStatus{disabled maxRows maxSharePct rules{${RETENTION_RULE_FIELDS}}}}`).then(d => d.retentionStatus),
  retentionLog: (ruleKey?: string | null, limit = 50) =>
    gql<{ retentionLog: RetentionLogEntry[] }>(`query($ruleKey:String,$limit:Int){retentionLog(ruleKey:$ruleKey,limit:$limit){id ruleKey label trigger mode status matched affected files filesFailed error actor startedAt finishedAt}}`, { ruleKey: ruleKey ?? null, limit }).then(d => d.retentionLog),
  updateRetentionRule: (key: string, patch: { days?: number; enabled?: boolean; mode?: 'preview' | 'live' }) =>
    gql<{ updateRetentionRule: RetentionRule }>(`mutation($key:ID!,$days:Int,$enabled:Boolean,$mode:String){updateRetentionRule(key:$key,days:$days,enabled:$enabled,mode:$mode){${RETENTION_RULE_FIELDS}}}`,
      { key, days: patch.days ?? null, enabled: patch.enabled ?? null, mode: patch.mode ?? null }).then(d => d.updateRetentionRule),
  runRetentionRule: (key: string, dryRun: boolean, allowLarge = false) =>
    gql<{ runRetentionRule: RetentionRunResult }>(`mutation($key:ID!,$dryRun:Boolean!,$allowLarge:Boolean){runRetentionRule(key:$key,dryRun:$dryRun,allowLarge:$allowLarge){ruleKey mode status matched affected files filesFailed message}}`,
      { key, dryRun, allowLarge }).then(d => d.runRetentionRule),
  // AD1: bokföring i Fortnox (bara plattformsadmin; servern kontrollerar).
  bookkeepingStatus: () =>
    gql<{ bookkeepingStatus: BookkeepingStatus }>(`query{bookkeepingStatus{${BOOKKEEPING_STATUS_FIELDS}}}`).then(d => d.bookkeepingStatus),
  bookkeepingPreview: (from: string, to: string) =>
    gql<{ bookkeepingPreview: BookkeepingPreview }>(`query($from:String!,$to:String!){bookkeepingPreview(from:$from,to:$to){drafts{day category description eventCount balanced ${VOUCHER_ROW_FIELDS}} vouchers{${FORTNOX_VOUCHER_FIELDS}} events{id day kind category occurredAt markId markName referenceId description gross fee vat owner cash backfilled booked} backfilledCount}}`, { from, to }).then(d => d.bookkeepingPreview),
  bookkeepingCheck: () => gql<{ bookkeepingCheck: string[] }>('query{bookkeepingCheck}').then(d => d.bookkeepingCheck),
  bookkeepingLog: (limit = 100) =>
    gql<{ bookkeepingLog: BookkeepingLogEntry[] }>('query($limit:Int){bookkeepingLog(limit:$limit){id voucherId day category status message actor createdAt}}', { limit }).then(d => d.bookkeepingLog),
  fortnoxConnectUrl: () => gql<{ fortnoxConnectUrl: string }>('mutation{fortnoxConnectUrl}').then(d => d.fortnoxConnectUrl),
  disconnectFortnox: () =>
    gql<{ disconnectFortnox: BookkeepingStatus }>(`mutation{disconnectFortnox{${BOOKKEEPING_STATUS_FIELDS}}}`).then(d => d.disconnectFortnox),
  updateBookkeepingSettings: (voucherSeries: string, accounts: BookkeepingAccounts, autoBookEnabled?: boolean) =>
    gql<{ updateBookkeepingSettings: BookkeepingStatus }>(`mutation($voucherSeries:String!,$accounts:BookkeepingAccountsInput!,$autoBookEnabled:Boolean){updateBookkeepingSettings(voucherSeries:$voucherSeries,accounts:$accounts,autoBookEnabled:$autoBookEnabled){${BOOKKEEPING_STATUS_FIELDS}}}`,
      { voucherSeries, accounts, autoBookEnabled: autoBookEnabled ?? null }).then(d => d.updateBookkeepingSettings),
  backfillLedger: () => gql<{ backfillLedger: number }>('mutation{backfillLedger}').then(d => d.backfillLedger),
  bookPeriod: (from: string, to: string) =>
    gql<{ bookPeriod: FortnoxVoucher[] }>(`mutation($from:String!,$to:String!){bookPeriod(from:$from,to:$to,confirm:true){${FORTNOX_VOUCHER_FIELDS}}}`, { from, to }).then(d => d.bookPeriod),
  retryFortnoxVoucher: (id: string) =>
    gql<{ retryFortnoxVoucher: FortnoxVoucher }>(`mutation($id:ID!){retryFortnoxVoucher(id:$id){${FORTNOX_VOUCHER_FIELDS}}}`, { id }).then(d => d.retryFortnoxVoucher),
  publishedAds: (kind?: AdKind) =>
    gql<{ publishedAds: MarkAd[] }>(`query($kind:String){publishedAds(kind:$kind){${AD_FIELDS} mark{${MARK_FIELDS_FOR_ADS}}}}`, { kind: kind ?? null }).then(d => d.publishedAds),
  markAd: (id: string) =>
    gql<{ markAd: MarkAd | null }>(`query($id:ID!){markAd(id:$id){${AD_FIELDS} mark{${MARK_FIELDS_FOR_ADS}}}}`, { id }).then(d => d.markAd),
  saveMarkAd: (input: MarkAdInput) =>
    gql<{ saveMarkAd: MarkAd }>(`mutation($input:MarkAdInput!){saveMarkAd(input:$input){${AD_FIELDS}}}`, { input }).then(d => d.saveMarkAd),
  publishMarkAd: (id: string, publish: boolean) =>
    gql<{ publishMarkAd: MarkAd }>(`mutation($id:ID!,$publish:Boolean!){publishMarkAd(id:$id,publish:$publish){${AD_FIELDS}}}`, { id, publish }).then(d => d.publishMarkAd),
  deleteMarkAd: (id: string) =>
    gql<{ deleteMarkAd: boolean }>(`mutation($id:ID!){deleteMarkAd(id:$id)}`, { id }).then(d => d.deleteMarkAd),
  editableMarks: () =>
    gql<{ editableMarks: EditableMark[] }>(`query{editableMarks{id name groups{id markId name color memberIds createdAt}}}`).then(d => d.editableMarks),
  dashboard: (jaktlagId?: string) =>
    gql<{ dashboard: DashboardStats }>(`
      query Dashboard($jaktlagId: ID) {
        dashboard(jaktlagId: $jaktlagId) {
          totalMarks totalJaktlag activeJakter totalKills totalKillsThisSeason
          goalProgress { gameType target current percent }
          recentObservations { id gameType count observedAt }
          recentKills { id gameType actualWeightKg shotAt }
        }
      }`, { jaktlagId }).then(d => d.dashboard),

  marks: (publishedOnly?: boolean) =>
    gql<{ marks: Mark[] }>(`query Marks($publishedOnly: Boolean) { marks(publishedOnly: $publishedOnly) { id name description areaHa county municipality address contactEmail contactPhone contactName imageUrl galleryUrls videoUrl pricePerYear gameTypesJson featured mapLat mapLng speciesOccurrenceJson facilitiesJson highlightsJson includesJson difficulty annualDrivenHunts afterhuntHuntersCount isPublished qregistryId importSource importSourceUrl importedAt costModel { markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt fallavgifter { viltslag kon typ kostnad } kottkostnad { viltslag kostnadPerKg } kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt } calendarEvents { id markId title description eventType startTime endTime location createdBy createdAt status invites { id email name inviteType status } } createdAt } }`, { publishedOnly })
      .then(d => d.marks),

  markById: (id: string) =>
    gql<{ mark: Mark }>(`query MarkById($id: ID!) { mark(id: $id) { id name description areaHa county municipality coordinatesJson address ownerUserId contactEmail contactPhone contactName imageUrl galleryUrls videoUrl pricePerYear gameTypesJson featured mapLat mapLng speciesOccurrenceJson facilitiesJson highlightsJson includesJson difficulty annualDrivenHunts afterhuntHuntersCount isPublished qregistryId importSource importSourceUrl importedAt costModel { markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt fallavgifter { viltslag kon typ kostnad } kottkostnad { viltslag kostnadPerKg } kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt } calendarEvents { id markId title description eventType startTime endTime location createdBy createdAt status invites { id email name inviteType status } } createdAt } }`, { id })
      .then(d => d.mark),

  setMarkPublished: (id: string, isPublished: boolean) =>
    gql<{ setMarkPublished: Mark }>(`mutation SetMarkPublished($id: ID!, $isPublished: Boolean!) { setMarkPublished(id: $id, isPublished: $isPublished) { id isPublished } }`, { id, isPublished })
      .then(d => d.setMarkPublished),

  deleteMark: (id: string) =>
    gql<{ deleteMark: boolean }>(`mutation DelMark($id: ID!) { deleteMark(id: $id) }`, { id }).then(d => d.deleteMark),

  deleteJakt: (id: string) =>
    gql<{ deleteJakt: boolean }>(`mutation DelJakt($id: ID!) { deleteJakt(id: $id) }`, { id }).then(d => d.deleteJakt),

  deleteHuntListing: (id: string) =>
    gql<{ deleteHuntListing: boolean }>(`mutation DelHuntListing($id: ID!) { deleteHuntListing(id: $id) }`, { id }).then(d => d.deleteHuntListing),

  createMark: (input: Partial<Mark>) =>
    gql<{ createMark: Mark }>(`
      mutation CreateMark($input: CreateMarkInput!) { createMark(input: $input) { id name areaHa county municipality createdAt } }
    `, { input }).then(d => d.createMark),

  jaktlag: () =>
    gql<{ jaktlag: Jaktlag[] }>(`query { jaktlag { id name description season maxMembers recruiting contactEmail createdAt } }`)
      .then(d => d.jaktlag),

  createJaktlag: (input: Partial<Jaktlag>) =>
    gql<{ createJaktlag: Jaktlag }>(`
      mutation CreateJaktlag($input: CreateJaktlagInput!) { createJaktlag(input: $input) { id name season createdAt } }
    `, { input }).then(d => d.createJaktlag),

  members: (jaktlagId: string) =>
    gql<{ members: JaktlagMember[] }>(`
      query Members($jaktlagId: ID!) { members(jaktlagId: $jaktlagId) { id name email role dogHandler afterhuntCertified status invitedAt joinedAt } }
    `, { jaktlagId }).then(d => d.members),

  inviteMember: (jaktlagId: string, input: Partial<JaktlagMember>) =>
    gql<{ inviteMember: { member: JaktlagMember; tempPassword: string; expiresAt: string } }>(`
      mutation InviteMember($jaktlagId: ID!, $input: InviteMemberInput!) {
        inviteMember(jaktlagId: $jaktlagId, input: $input) { member { id name email role status } tempPassword expiresAt }
      }
    `, { jaktlagId, input }).then(d => d.inviteMember),

  jakter: (jaktlagId?: string, status?: string) =>
    gql<{ jakter: Jakt[] }>(`
      query Jakter($jaktlagId: ID, $status: JaktStatus) { jakter(jaktlagId: $jaktlagId, status: $status) { id title gameTypes startTime endTime status meetingPoint qregistryId createdBy createdAt } }
    `, { jaktlagId, status }).then(d => d.jakter),

  createJakt: (input: Partial<Jakt> & { createdBy: string }) =>
    gql<{ createJakt: Jakt }>(`
      mutation CreateJakt($input: CreateJaktInput!) { createJakt(input: $input) { id title startTime status createdAt } }
    `, { input }).then(d => d.createJakt),

  startJakt: (id: string) =>
    gql<{ startJakt: Jakt }>(`mutation StartJakt($id: ID!) { startJakt(id: $id) { id status } }`, { id }).then(d => d.startJakt),

  endJakt: (id: string) =>
    gql<{ endJakt: Jakt }>(`mutation EndJakt($id: ID!) { endJakt(id: $id) { id status endTime } }`, { id }).then(d => d.endJakt),

  poster: (jaktId: string) =>
    gql<{ poster: Post[] }>(`
      query Poster($jaktId: ID!) { poster(jaktId: $jaktId) { id name latitude longitude assignedMemberId assignedMember { name } sector notes } }
    `, { jaktId }).then(d => d.poster),

  createPost: (input: Partial<Post>) =>
    gql<{ createPost: Post }>(`
      mutation CreatePost($input: CreatePostInput!) { createPost(input: $input) { id name latitude longitude sector } }
    `, { input }).then(d => d.createPost),

  observationer: (jaktId: string) =>
    gql<{ observationer: Observation[] }>(`
      query Obs($jaktId: ID!) { observationer(jaktId: $jaktId) { id gameType count sex ageClass direction observedAt notes } }
    `, { jaktId }).then(d => d.observationer),

  recordObservation: (input: Partial<Observation>) =>
    gql<{ recordObservation: Observation }>(`
      mutation RecordObs($input: RecordObservationInput!) { recordObservation(input: $input) { id gameType count observedAt } }
    `, { input }).then(d => d.recordObservation),

  avskjutningar: (jaktId: string) =>
    gql<{ avskjutningar: Avskjutning[] }>(`
      query Kills($jaktId: ID) { avskjutningar(jaktId: $jaktId) { id gameType sex ageClass actualWeightKg estimatedWeightKg shotAt fieldFee kgPrice totalMeatValue notes member { name } } }
    `, { jaktId }).then(d => d.avskjutningar),

  registerKill: (input: Partial<Avskjutning> & { shotAt: string }) =>
    gql<{ registerKill: Avskjutning }>(`
      mutation RegisterKill($input: RegisterKillInput!) { registerKill(input: $input) { id gameType shotAt totalMeatValue } }
    `, { input }).then(d => d.registerKill),

  skjutmenyer: (jaktId: string) =>
    gql<{ skjutmenyer: Skjutmeny[] }>(`
      query Menu($jaktId: ID!) { skjutmenyer(jaktId: $jaktId) { id gameType maxCount fieldFee kgPrice alreadyShot remaining member { id name } } }
    `, { jaktId }).then(d => d.skjutmenyer),

  setSkjutmeny: (input: Partial<Skjutmeny>) =>
    gql<{ setSkjutmeny: Skjutmeny }>(`
      mutation SetMenu($input: SetSkjutmenyInput!) { setSkjutmeny(input: $input) { id gameType maxCount remaining } }
    `, { input }).then(d => d.setSkjutmeny),

  avskjutningsmal: (jaktlagId: string, season?: string) =>
    gql<{ avskjutningsmal: AvskjutningsMal[] }>(`
      query Mal($jaktlagId: ID!, $season: String) { avskjutningsmal(jaktlagId: $jaktlagId, season: $season) { id season gameType targetCount currentCount status progressPercent } }
    `, { jaktlagId, season }).then(d => d.avskjutningsmal),

  createMal: (input: { jaktlagId: string; season: string; gameType: string; targetCount: number }) =>
    gql<{ createAvskjutningsmal: AvskjutningsMal }>(`
      mutation CreateMal($input: CreateMalInput!) { createAvskjutningsmal(input: $input) { id gameType targetCount currentCount } }
    `, { input }).then(d => d.createAvskjutningsmal),

  liveHuntStatus: (jaktId: string) =>
    gql<{ liveHuntStatus: LiveHuntStatus }>(`
      query Live($jaktId: ID!) {
        liveHuntStatus(jaktId: $jaktId) {
          jaktId status activePoster totalObservations totalKills lastObservationAt
          participants { postId postName memberName latitude longitude observationCount killCount }
        }
      }
    `, { jaktId }).then(d => d.liveHuntStatus),

  scrapedTeams: (county?: string, recruiting?: boolean) =>
    gql<{ scrapedTeams: ScrapedTeam[] }>(`
      query Scraped($county: String, $recruiting: Boolean) {
        scrapedTeams(county: $county, recruiting: $recruiting) { id name county municipality areaHa memberCount recruiting gameTypes scrapedAt }
      }
    `, { county, recruiting }).then(d => d.scrapedTeams),

  // ── MARKETPLACE ─────────────────────────────────────────────────
  huntListings: (gameType?: string, region?: string) =>
    gql<{ huntListings: HuntListing[] }>(`
      query HuntListings($gameType: String, $region: String) {
        huntListings(gameType: $gameType, region: $region) {
          id title subtitle description location region county huntType
          gameTypes primaryGameType startDate endDate imageUrl
          price priceUnit spotsTotal spotsAvailable difficulty
          includes highlights featured status contactName contactEmail
          speciesOccurrenceJson facilitiesJson
          annualDrivenHunts afterhuntHuntersCount
          mapLat mapLng swishNumber swishAmount markId createdAt
        }
      }`, { gameType, region }).then(d => d.huntListings),

  huntListing: (id: string) =>
    gql<{ huntListing: HuntListing }>(`
      query HuntListing($id: ID!) {
        huntListing(id: $id) {
          id title subtitle description location region county huntType
          gameTypes primaryGameType startDate endDate imageUrl
          price priceUnit spotsTotal spotsAvailable difficulty
          includes highlights featured status contactName contactEmail
          speciesOccurrenceJson facilitiesJson
          annualDrivenHunts afterhuntHuntersCount
          mapLat mapLng swishNumber swishAmount markId createdAt
        }
      }`, { id }).then(d => d.huntListing),

  createBooking: (input: { listingId: string; hunterName: string; hunterEmail: string; hunterPhone?: string; hunterLicenseNo?: string; notes?: string }) =>
    gql<{ createBooking: Booking }>(`
      mutation CreateBooking($input: CreateBookingInput!) {
        createBooking(input: $input) { id listingId hunterName hunterEmail status createdAt }
      }`, { input }).then(d => d.createBooking),

  signContract: (bookingId: string, signatureName: string, signatureData?: string) =>
    gql<{ signContract: Booking }>(`
      mutation SignContract($bookingId: ID!, $signatureName: String!, $signatureData: String) {
        signContract(bookingId: $bookingId, signatureName: $signatureName, signatureData: $signatureData) {
          id status contractSignedAt signatureName
        }
      }`, { bookingId, signatureName, signatureData }).then(d => d.signContract),

  myMarks: () =>
    gql<{ myMarks: Mark[] }>(`query { myMarks { id isDemo name description areaHa county municipality coordinatesJson address ownerUserId contactEmail contactName imageUrl galleryUrls videoUrl pricePerYear gameTypesJson featured mapLat mapLng speciesOccurrenceJson facilitiesJson highlightsJson includesJson difficulty annualDrivenHunts afterhuntHuntersCount costModel { markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt fallavgifter { viltslag kon typ kostnad } kottkostnad { viltslag kostnadPerKg } kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt } calendarEvents { id markId title description eventType startTime endTime location createdBy createdAt status invites { id email name inviteType status } } createdAt } }`)
      .then(d => d.myMarks),

  myHuntBookings: () =>
    gql<{ myHuntBookings: Booking[] }>(`query { myHuntBookings { id listingId hunterName hunterEmail status contractSignedAt createdAt listing { id title subtitle description location region imageUrl price priceUnit primaryGameType gameTypes startDate endDate } } }`)
      .then(d => d.myHuntBookings),

  // D5: äldre ansökningar till marken (skrivskyddade) – läses, godkänns (bara statusen) eller avslås.
  markApplications: (markId: string) =>
    gql<{ markApplications: MarkApplication[] }>(`query($markId:ID!){markApplications(markId:$markId){id markId applicantEmail applicantName applicationText meetsRequirements status reviewNote adminQuestion applicantResponse createdAt updatedAt}}`, { markId })
      .then(d => d.markApplications),
  reviewApplication: (applicationId: string, status: 'approved' | 'rejected', reviewNote?: string) =>
    gql<{ reviewApplication: MarkApplication }>(`mutation($id:ID!,$status:String!,$note:String){reviewApplication(applicationId:$id,status:$status,reviewNote:$note){id status reviewNote}}`, { id: applicationId, status, note: reviewNote ?? null })
      .then(d => d.reviewApplication),
  myApplications: () =>
    gql<{ myApplications: MarkApplication[] }>(`query { myApplications { id markId applicantEmail applicantName applicationText meetsRequirements status reviewNote adminQuestion applicantResponse contract { id applicationId markId applicantEmail contractText amount swishNumber status signedAt signatureName paymentStatus paymentConfirmedAt paymentReference createdAt updatedAt } createdAt updatedAt } }`)
      .then(d => d.myApplications),

  signApplicationContract: (contractId: string, signatureName: string) =>
    gql<{ signApplicationContract: ApplicationContract }>(`mutation SC($id: ID!, $name: String!) { signApplicationContract(contractId: $id, signatureName: $name) { id markId applicationId status signedAt signatureName amount swishNumber paymentStatus } }`, { id: contractId, name: signatureName })
      .then(d => d.signApplicationContract),

  confirmPayment: (contractId: string, paymentReference?: string) =>
    gql<{ confirmPayment: ApplicationContract }>(`mutation CP($id: ID!, $ref: String) { confirmPayment(contractId: $id, paymentReference: $ref) { id paymentStatus } }`, { id: contractId, ref: paymentReference })
      .then(d => d.confirmPayment),


  markContracts: (markId: string) =>
    gql<{ markContracts: ApplicationContract[] }>(`query MC($id: ID!) { markContracts(markId: $id) { id applicantEmail contractText amount swishNumber status signedAt signatureName paymentStatus createdAt updatedAt } }`, { id: markId })
      .then(d => d.markContracts),

  contractTemplates: (markId: string) =>
    gql<{ contractTemplates: ContractTemplate[] }>(`query CT($id: ID!) { contractTemplates(markId: $id) { id markId name contractText amount swishNumber isDefault createdBy createdAt updatedAt } }`, { id: markId })
      .then(d => d.contractTemplates),

  createContractTemplate: (markId: string, name: string, contractText: string, amount: number, swishNumber?: string, isDefault?: boolean) =>
    gql<{ createContractTemplate: ContractTemplate }>(`mutation CCT($markId: ID!, $name: String!, $text: String!, $amount: Float!, $sw: String, $def: Boolean) { createContractTemplate(markId: $markId, name: $name, contractText: $text, amount: $amount, swishNumber: $sw, isDefault: $def) { id markId name contractText amount swishNumber isDefault createdAt updatedAt } }`, { markId, name, text: contractText, amount, sw: swishNumber, def: isDefault })
      .then(d => d.createContractTemplate),

  updateContractTemplate: (id: string, fields: { name?: string; contractText?: string; amount?: number; swishNumber?: string; isDefault?: boolean }) =>
    gql<{ updateContractTemplate: ContractTemplate }>(`mutation UCT($id: ID!, $name: String, $text: String, $amount: Float, $sw: String, $def: Boolean) { updateContractTemplate(id: $id, name: $name, contractText: $text, amount: $amount, swishNumber: $sw, isDefault: $def) { id name contractText amount swishNumber isDefault updatedAt } }`, { id, name: fields.name, text: fields.contractText, amount: fields.amount, sw: fields.swishNumber, def: fields.isDefault })
      .then(d => d.updateContractTemplate),

  deleteContractTemplate: (id: string) =>
    gql<{ deleteContractTemplate: boolean }>(`mutation DCT($id: ID!) { deleteContractTemplate(id: $id) }`, { id })
      .then(d => d.deleteContractTemplate),
  subscribeMark: (markId: string) =>
    gql<{ subscribeMark: boolean }>(`mutation SubscribeMark($markId: ID!) { subscribeMark(markId: $markId) }`, { markId })
      .then(d => d.subscribeMark),

  unsubscribeMark: (markId: string) =>
    gql<{ unsubscribeMark: boolean }>(`mutation UnsubscribeMark($markId: ID!) { unsubscribeMark(markId: $markId) }`, { markId })
      .then(d => d.unsubscribeMark),

  triggerScraper: async (county?: string, maxPages = 3) => {
    const res = await fetch(resolveUrl('/api/scraper/run'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ county, maxPages }),
    });
    return res.json();
  },

  aiSearch: async (query: string): Promise<{ message: string; count: number; results: HuntListing[] }> => {
    const res = await fetch(resolveUrl('/api/ai/search'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!res.ok) throw new Error('AI search failed');
    return res.json();
  },

  aiChat: async (message: string, userId?: string): Promise<{
    message: string;
    intent: {
      action: 'search' | 'navigate' | 'start_hunt' | 'create_plan' | 'sell_meat' | 'buy_meat' | 'create_event' | 'open_mark' | 'open_profile' | 'unknown';
      params: Record<string, string>;
      message: string;
      suggestions?: string[];
    };
    meatResults?: Array<{
      id: string; species: string; cut: string; weightG: number;
      pricePerKg: number; totalPrice: number; location: string;
      postalCode: string; shotAt: string; seller: string;
      imageUrl: string; swish: boolean; frozen: boolean;
      description?: string;
    }>;
    userMarks: Array<{id: string; title: string}>;
  }> => {
    const res = await fetch(resolveUrl('/api/ai/chat'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, userId }),
    });
    if (!res.ok) throw new Error('AI chat failed');
    return res.json();
  },

  // ── FILE UPLOAD ───────────────────────────────────────────────────
  uploadFile: async (file: File): Promise<{id: string; filename: string; url: string; type: string; originalName: string; size: number}> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(resolveUrl('/api/upload'), {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    const j = await res.json() as { id: string; filename: string; url: string; type: string; originalName: string; size: number };
    j.url = absolutizeUploadUrl(j.url);
    return j;
  },

  uploadFiles: async (files: File[]): Promise<{files: Array<{id: string; filename: string; url: string; type: string}>; count: number}> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await fetch(resolveUrl('/api/upload/multiple'), {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    const j = await res.json() as { files: Array<{ id: string; filename: string; url: string; type: string }>; count: number };
    j.files = j.files.map(f => ({ ...f, url: absolutizeUploadUrl(f.url) }));
    return j;
  },

  listUploads: async (): Promise<{images: Array<{filename: string; url: string; size: number; createdAt: number}>; videos: Array<{filename: string; url: string; size: number; createdAt: number}>; totalImages: number; totalVideos: number}> => {
    const res = await fetch(resolveUrl('/api/upload/list'));
    if (!res.ok) throw new Error('Failed to list uploads');
    return res.json();
  },

  // ── UPDATE MUTATIONS ──────────────────────────────────────────────
  updateHuntListing: (id: string, input: Partial<HuntListing> & {galleryUrls?: string[]; videoUrl?: string}) =>
    gql<{ updateHuntListing: HuntListing }>(`
      mutation UpdateHuntListing($id: ID!, $input: UpdateHuntListingInput!) {
        updateHuntListing(id: $id, input: $input) {
          id title subtitle description location region county
          gameTypes primaryGameType startDate endDate imageUrl
          price priceUnit spotsTotal spotsAvailable difficulty
          includes highlights featured status contactName contactEmail
          speciesOccurrenceJson facilitiesJson
          annualDrivenHunts afterhuntHuntersCount
          mapLat mapLng galleryUrls videoUrl createdAt
        }
      }`, { id, input }).then(d => d.updateHuntListing),

  updateMark: (id: string, input: Partial<Mark> & {galleryUrls?: string[]; videoUrl?: string}) =>
    gql<{ updateMark: Mark }>(`
      mutation UpdateMark($id: ID!, $input: UpdateMarkInput!) {
        updateMark(id: $id, input: $input) { id name description areaHa county municipality coordinatesJson address contactEmail contactPhone imageUrl galleryUrls videoUrl pricePerYear gameTypesJson featured mapLat mapLng speciesOccurrenceJson facilitiesJson highlightsJson includesJson difficulty annualDrivenHunts afterhuntHuntersCount contactName createdAt updatedAt }
      }`, { id, input }).then(d => d.updateMark),

  markCostModel: (markId: string) =>
    gql<{ markCostModel: MarkCostModel | null }>(`
      query MarkCostModel($markId: ID!) {
        markCostModel(markId: $markId) {
          markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt
          fallavgifter { viltslag kon typ kostnad }
          kottkostnad { viltslag kostnadPerKg }
          kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt
        }
      }`, { markId }).then(d => d.markCostModel),

  upsertMarkCostModel: (markId: string, input: Omit<MarkCostModel,'markId'|'updatedAt'>) =>
    gql<{ upsertMarkCostModel: MarkCostModel }>(`
      mutation UpsertMarkCostModel($markId: ID!, $input: UpsertMarkCostModelInput!) {
        upsertMarkCostModel(markId: $markId, input: $input) {
          markId arsavgift viltvardAvgiftPerAr gastKostnadPerJakt medlemKostnadPerJakt
          fallavgifter { viltslag kon typ kostnad }
          kottkostnad { viltslag kostnadPerKg }
          kottIngar antalDrevjakter kanBokaKalendar boterOlovligtVilt boterEftersok updatedAt
        }
      }`, { markId, input }).then(d => d.upsertMarkCostModel),

  // ── ARRENDE SETTINGS ──────────────────────────────────────────
  arrendeSettings: () =>
    gql<{ arrendeSettings: ArrendeSettings | null }>(`
      query { arrendeSettings { speciesFallJson subcatFallJson meatKgJson prisDrev prisAlg prisSmyg prisAtel prisFagel updatedAt } }
    `).then(d => d.arrendeSettings),

  upsertArrendeSettings: (input: Omit<ArrendeSettings, 'updatedAt'>) =>
    gql<{ upsertArrendeSettings: ArrendeSettings }>(`
      mutation UpsertArrendeSettings($input: UpsertArrendeSettingsInput!) {
        upsertArrendeSettings(input: $input) {
          speciesFallJson subcatFallJson meatKgJson prisDrev prisAlg prisSmyg prisAtel prisFagel updatedAt
        }
      }`, { input }).then(d => {
        if (!d?.upsertArrendeSettings) throw new Error('API saknar stöd för arrendeinställningar — uppdatera servern.');
        return d.upsertArrendeSettings;
      }),

  // ── EQUIPMENT ─────────────────────────────────────────────────
  myEquipment: () =>
    gql<{ myEquipment: UserEquipment[] }>(`query { myEquipment { id userId name category description imageUrl condition forRent rentPrice forSale salePrice qregistryObjectId status createdAt updatedAt } }`)
      .then(d => d.myEquipment),

  createEquipment: (input: Partial<UserEquipment>) =>
    gql<{ createEquipment: UserEquipment }>(`
      mutation CreateEquipment($input: CreateEquipmentInput!) { createEquipment(input: $input) { id name category description imageUrl condition forRent rentPrice forSale salePrice status createdAt } }
    `, { input }).then(d => d.createEquipment),

  updateEquipment: (id: string, input: Partial<UserEquipment>) =>
    gql<{ updateEquipment: UserEquipment }>(`
      mutation UpdateEquipment($id: ID!, $input: UpdateEquipmentInput!) { updateEquipment(id: $id, input: $input) { id name category description imageUrl condition forRent rentPrice forSale salePrice status createdAt } }
    `, { id, input }).then(d => d.updateEquipment),

  deleteEquipment: (id: string) =>
    gql<{ deleteEquipment: boolean }>(`
      mutation DeleteEquipment($id: ID!) { deleteEquipment(id: $id) }
    `, { id }).then(d => d.deleteEquipment),

  // ── WEAPONS ───────────────────────────────────────────────────
  myWeapons: () =>
    gql<{ myWeapons: UserWeapon[] }>(`query { myWeapons { id userId name weaponType manufacturer model serialNumber caliber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack description imageUrl condition forRent rentPrice forSale salePrice status createdAt updatedAt licenses { id weaponId partType serialNumber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack caliber notes createdAt } } }`)
      .then(d => d.myWeapons || []),

  createWeapon: (input: Partial<UserWeapon> & { name: string }) =>
    gql<{ createWeapon: UserWeapon }>(`mutation CreateWeapon($input: CreateWeaponInput!) { createWeapon(input: $input) { id userId name weaponType manufacturer model serialNumber caliber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack description imageUrl condition forRent rentPrice forSale salePrice status createdAt updatedAt } }`, { input })
      .then(d => d.createWeapon),

  updateWeapon: (id: string, input: Partial<UserWeapon>) =>
    gql<{ updateWeapon: UserWeapon }>(`mutation UpdateWeapon($id: ID!, $input: UpdateWeaponInput!) { updateWeapon(id: $id, input: $input) { id userId name weaponType manufacturer model serialNumber caliber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack description imageUrl condition forRent rentPrice forSale salePrice status createdAt updatedAt } }`, { id, input })
      .then(d => d.updateWeapon),

  deleteWeapon: (id: string) =>
    gql<{ deleteWeapon: boolean }>(`mutation DeleteWeapon($id: ID!) { deleteWeapon(id: $id) }`, { id })
      .then(d => d.deleteWeapon),

  createWeaponLoan: (input: Partial<WeaponLoan> & { weaponId: string }) =>
    gql<{ createWeaponLoan: WeaponLoan }>(`mutation CreateWeaponLoan($input: CreateWeaponLoanInput!) { createWeaponLoan(input: $input) { id weaponId lenderUserId borrowerName borrowerEmail borrowerPhone borrowerPhoneMobile borrowerAddress borrowerPostalCode borrowerCity borrowerPersonnummer purpose loanStart loanEnd notes status createdAt } }`, { input })
      .then(d => d.createWeaponLoan),

  ocrWeaponLicense: async (imageUrl: string): Promise<{ success: boolean; data: Partial<UserWeapon>; error?: string }> => {
    const token = getAccessToken();
    if (!token) throw new Error('Autentisering krävs');
    const res = await fetch(resolveUrl('/api/weapons/ocr-license'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders(token) },
      body: JSON.stringify({ imageUrl }),
    });
    return res.json();
  },

  ocrWeaponLicenseDual: async (imageUrl: string, imageUrlBack?: string, licensePart?: string): Promise<{ success: boolean; data: Partial<UserWeapon & { licensePart?: string }>; error?: string }> => {
    const token = getAccessToken();
    if (!token) throw new Error('Autentisering krävs');
    const res = await fetch(resolveUrl('/api/weapons/ocr-license'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders(token) },
      body: JSON.stringify({ imageUrl, imageUrlBack, licensePart }),
    });
    return res.json();
  },

  weaponLicenses: (weaponId: string) =>
    gql<{ weaponLicenses: WeaponLicense[] }>(`query($weaponId: ID!) { weaponLicenses(weaponId: $weaponId) { id weaponId partType serialNumber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack caliber notes createdAt } }`, { weaponId })
      .then(d => d.weaponLicenses || []),

  createWeaponLicense: (input: Partial<WeaponLicense> & { weaponId: string }) =>
    gql<{ createWeaponLicense: WeaponLicense }>(`mutation CreateWeaponLicense($input: CreateWeaponLicenseInput!) { createWeaponLicense(input: $input) { id weaponId partType serialNumber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack caliber notes createdAt } }`, { input })
      .then(d => d.createWeaponLicense),

  updateWeaponLicense: (id: string, input: Partial<WeaponLicense>) =>
    gql<{ updateWeaponLicense: WeaponLicense }>(`mutation UpdateWeaponLicense($id: ID!, $input: UpdateWeaponLicenseInput!) { updateWeaponLicense(id: $id, input: $input) { id weaponId partType serialNumber licenseNumber licenseType licenseHolder licenseExpires licenseImageUrl licenseImageUrlBack caliber notes createdAt } }`, { id, input })
      .then(d => d.updateWeaponLicense),

  deleteWeaponLicense: (id: string) =>
    gql<{ deleteWeaponLicense: boolean }>(`mutation DeleteWeaponLicense($id: ID!) { deleteWeaponLicense(id: $id) }`, { id })
      .then(d => d.deleteWeaponLicense),

  ocrHunterExam: async (imageUrl: string): Promise<{ success: boolean; data: any; error?: string }> => {
    const token = getAccessToken();
    if (!token) throw new Error('Autentisering krävs');
    const res = await fetch(resolveUrl('/api/weapons/ocr-hunter-exam'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders(token) },
      body: JSON.stringify({ imageUrl }),
    });
    return res.json();
  },

  // ── MARK COMMUNITY ──────────────────────────────────────────────
  markRoles: () =>
    gql<{ markRoles: MarkRolePermissions[] }>(`query { markRoles { role label canManageMembers canEditMark canManageCalendar canManageShootingPlan canChat canViewMap canEditMap canStartHunt canRecordKill canManageMeatSharing canPayFees canViewAlbum canUploadAlbum canManageIntegrations canInviteMembers canRemoveMembers canAssignRoles } }`).then(d => d.markRoles),

  myMarkRole: (markId: string) =>
    gql<{ myMarkRole: MarkRolePermissions }>(`query MyRole($markId: ID!) { myMarkRole(markId: $markId) { role label canManageMembers canEditMark canManageCalendar canManageShootingPlan canChat canViewMap canEditMap canStartHunt canRecordKill canManageMeatSharing canPayFees canViewAlbum canUploadAlbum canManageIntegrations canInviteMembers canRemoveMembers canAssignRoles } }`, { markId }).then(d => d.myMarkRole),

  markMembers: (markId: string) =>
    gql<{ markMembers: MarkMember[] }>(`query MarkMembers($markId: ID!) { markMembers(markId: $markId) { id markId userEmail name role avatarUrl phone dogHandler joinedAt status permissions { role label canManageMembers canEditMark canManageCalendar canManageShootingPlan canChat canViewMap canEditMap canStartHunt canRecordKill canManageMeatSharing canViewAlbum canUploadAlbum canManageIntegrations canInviteMembers canRemoveMembers canAssignRoles } } }`, { markId }).then(d => d.markMembers),

  /** includeLodging: bokat boende som händelser med eventType 'boende' (markens kalenderflik). */
  markCalendarEvents: (markId: string, includeLodging = false) =>
    gql<{ markCalendarEvents: MarkCalendarEvent[] }>(`query MarkCal($markId: ID!, $lodging: Boolean) { markCalendarEvents(markId: $markId, includeLodging: $lodging) { id markId title description eventType startTime endTime location createdBy createdAt qmeetMeetingId status endedAt recordingUrl transcription summary observationsJson killsJson satPlanJson ${PLANNED_HUNT_FIELDS} invites { ${INVITE_FIELDS} } } }`, { markId, lodging: includeLodging }).then(d => d.markCalendarEvents),

  markChatMessages: (markId: string, limit = 50) =>
    gql<{ markChatMessages: MarkChatMessage[] }>(`query MarkChat($markId: ID!, $limit: Int) { markChatMessages(markId: $markId, limit: $limit) { id markId senderEmail senderName message imageUrl fileUrl fileName fileType messageType createdAt } }`, { markId, limit }).then(d =>
      Array.isArray(d.markChatMessages) ? d.markChatMessages : [],
    ),

  myChats: () =>
    gql<{ myChats: ChatSummary[] }>(`query MyChats { myChats { key kind markId title imageUrl lastMessage lastSenderName lastSenderEmail lastAt unreadCount } }`).then(d => Array.isArray(d.myChats) ? d.myChats : []),

  markChatRead: (key: string) =>
    gql<{ markChatRead: boolean }>(`mutation ChatRead($key: ID!) { markChatRead(key: $key) }`, { key }).then(d => d.markChatRead),

  markShootingPlan: (markId: string, season?: string) =>
    gql<{ markShootingPlan: MarkShootingPlanItem[] }>(`query ShootPlan($markId: ID!, $season: String) { markShootingPlan(markId: $markId, season: $season) { id markId season gameType plannedCount actualCount costPerAnimal notes createdAt updatedAt } }`, { markId, season }).then(d => d.markShootingPlan),

  markMeatSharing: (markId: string, season?: string) =>
    gql<{ markMeatSharing: MarkMeatSharingItem[] }>(`query MeatShare($markId: ID!, $season: String) { markMeatSharing(markId: $markId, season: $season) { id markId season gameType animalNumber shotByEmail shotByName shotDate weightKg sharedToEmail sharedToName portionKg notes createdAt } }`, { markId, season }).then(d => d.markMeatSharing),

  markMeatQueue: (markId: string, gameType?: string) =>
    gql<{ markMeatQueue: MarkMeatQueueItem[] }>(`query MeatQueue($markId: ID!, $gameType: String) { markMeatQueue(markId: $markId, gameType: $gameType) { id markId gameType memberEmail memberName queuePosition lastReceivedAt } }`, { markId, gameType }).then(d => d.markMeatQueue),

  markViltRegistrations: (markId: string, season?: string) =>
    gql<{ markViltRegistrations: MarkViltRegistration[] }>(`query ViltReg($markId: ID!, $season: String) { markViltRegistrations(markId: $markId, season: $season) { id markId season gameType animalNumber djurId handlingStatus falltViltPinId shotDate shotByEmail shotByName sex ageClass estimatedWeightKg disposition shareCount avpalsat slaktviktKg trikintestad cesiumtestad saleNotes fallavgiftKr kottkostnadPerKg viktKg notes createdAt updatedAt } }`, { markId, season }).then(d => d.markViltRegistrations),

  markFalltVilt: (markId: string) =>
    gql<{ markFalltVilt: any[] }>(`query MarkFalltVilt($markId: ID!) { markFalltVilt(markId: $markId) { id markId type lat lng name viltslag kon alder jagarEmail falltStatus qregistryObjectId photoUrls falltViltEvents { id eventType authorEmail oldStatus newStatus commentText createdAt } createdAt } }`, { markId }).then(d => d.markFalltVilt),

  addViltRegistration: (input: { markId: string; season: string; gameType: string; falltViltPinId?: string; shotDate?: string; shotByEmail?: string; shotByName?: string; sex?: string; ageClass?: string; estimatedWeightKg?: number; notes?: string; meetingId?: string }) =>
    gql<{ addViltRegistration: MarkViltRegistration }>(`mutation AddViltReg($input: AddViltRegistrationInput!) { addViltRegistration(input: $input) { id markId season gameType animalNumber djurId handlingStatus falltViltPinId shotDate shotByEmail shotByName sex ageClass estimatedWeightKg disposition shareCount avpalsat slaktviktKg trikintestad cesiumtestad saleNotes fallavgiftKr kottkostnadPerKg viktKg notes createdAt updatedAt } }`, { input }).then(d => d.addViltRegistration),

  updateViltHandlingStatus: (id: string, status: string, comment?: string, changedByName?: string) =>
    gql<{ updateViltHandlingStatus: ViltHandlingLog }>(`mutation UpdateViltHandling($id: ID!, $status: String!, $comment: String, $changedByName: String) { updateViltHandlingStatus(id: $id, status: $status, comment: $comment, changedByName: $changedByName) { id viltRegId oldStatus newStatus changedByEmail changedByName comment createdAt } }`, { id, status, comment, changedByName }).then(d => d.updateViltHandlingStatus),

  viltHandlingLog: (viltRegId: string) =>
    gql<{ viltHandlingLog: ViltHandlingLog[] }>(`query ViltHandlingLog($viltRegId: ID!) { viltHandlingLog(viltRegId: $viltRegId) { id viltRegId oldStatus newStatus changedByEmail changedByName comment createdAt } }`, { viltRegId }).then(d => d.viltHandlingLog),

  updateViltDisposition: (id: string, input: { disposition: string; shareCount?: number; avpalsat?: boolean; slaktviktKg?: number; trikintestad?: boolean; cesiumtestad?: boolean; saleNotes?: string; fallavgiftKr?: number; kottkostnadPerKg?: number; viktKg?: number }) =>
    gql<{ updateViltDisposition: MarkViltRegistration }>(`mutation UpdateViltDisp($id: ID!, $input: UpdateViltDispositionInput!) { updateViltDisposition(id: $id, input: $input) { id markId season gameType animalNumber disposition shareCount avpalsat slaktviktKg trikintestad cesiumtestad saleNotes fallavgiftKr kottkostnadPerKg viktKg notes createdAt updatedAt } }`, { id, input }).then(d => d.updateViltDisposition),

  distributeViltToList: (id: string, shareCount: number, memberEmails: string[], memberNames: string[]) =>
    gql<{ distributeViltToList: MarkMeatSharingItem[] }>(`mutation DistributeVilt($id: ID!, $shareCount: Int!, $memberEmails: [String!]!, $memberNames: [String!]!) { distributeViltToList(id: $id, shareCount: $shareCount, memberEmails: $memberEmails, memberNames: $memberNames) { id markId season gameType animalNumber shotByEmail shotByName shotDate weightKg sharedToEmail sharedToName portionKg notes createdAt } }`, { id, shareCount, memberEmails, memberNames }).then(d => d.distributeViltToList),

  deleteViltRegistration: (id: string) =>
    gql<{ deleteViltRegistration: boolean }>(`mutation DeleteViltReg($id: ID!) { deleteViltRegistration(id: $id) }`, { id }).then(d => d.deleteViltRegistration),

  addMarkMember: (markId: string, input: { userEmail: string; name: string; role?: string; phone?: string; dogHandler?: boolean; groupId?: string | null }) =>
    gql<{ addMarkMember: MarkMember }>(`mutation AddMember($markId: ID!, $input: AddMarkMemberInput!) { addMarkMember(markId: $markId, input: $input) { id name userEmail role status } }`, { markId, input }).then(d => d.addMarkMember),

  removeMarkMember: (markId: string, memberEmail: string) =>
    gql<{ removeMarkMember: boolean }>(`mutation RemoveMember($markId: ID!, $memberEmail: String!) { removeMarkMember(markId: $markId, memberEmail: $memberEmail) }`, { markId, memberEmail }).then(d => d.removeMarkMember),

  updateMarkMemberRole: (markId: string, memberId: string, role: string) =>
    gql<{ updateMarkMemberRole: MarkMember }>(`mutation UpdateRole($markId: ID!, $input: UpdateMarkMemberRoleInput!) { updateMarkMemberRole(markId: $markId, input: $input) { id name role userEmail permissions { role label canManageMembers canEditMark canManageCalendar canManageShootingPlan canChat canViewMap canEditMap canStartHunt canRecordKill canManageMeatSharing canViewAlbum canUploadAlbum canManageIntegrations canInviteMembers canRemoveMembers canAssignRoles } } }`, { markId, input: { memberId, role } }).then(d => d.updateMarkMemberRole),

  markGroups: async (markId: string): Promise<MarkGroup[]> => {
    const res = await gql<{ markGroups: MarkGroup[] }>(`query($id:ID!){markGroups(markId:$id){id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg maxJakttillfallen jagarKravJson isPublished publishedAt description kontaktEmail}}}`, { id: markId });
    // "Ännu ej godkända" (pending-<markId>) visas i antagningen (AdmissionsPanel), inte som vanlig grupp.
    return (res?.markGroups ?? []).filter(g => !g.id.startsWith('pending-'));
  },
  createMarkGroup: async (markId: string, name: string, color: string): Promise<MarkGroup> => {
    const res = await gql<{ createMarkGroup: MarkGroup }>(`mutation($markId:ID!,$input:CreateMarkGroupInput!){createMarkGroup(markId:$markId,input:$input){id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum}}}`, { markId, input: { name, color } });
    return res.createMarkGroup;
  },
  deleteMarkGroup: async (groupId: string): Promise<void> => {
    await gql<{ deleteMarkGroup: boolean }>(`mutation($id:ID!){deleteMarkGroup(groupId:$id)}`, { id: groupId });
  },
  renameMarkGroup: async (groupId: string, name: string, color: string): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson}`;
    const res = await gql<{ renameMarkGroup: MarkGroup }>(`mutation($id:ID!,$name:String!,$color:String){renameMarkGroup(groupId:$id,name:$name,color:$color){${f}}}`, { id: groupId, name, color });
    return res.renameMarkGroup;
  },
  addMemberToMarkGroup: async (groupId: string, memberId: string): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson}`;
    const res = await gql<{ addMemberToMarkGroup: MarkGroup }>(`mutation($gid:ID!,$mid:ID!){addMemberToMarkGroup(groupId:$gid,memberId:$mid){${f}}}`, { gid: groupId, mid: memberId });
    return res.addMemberToMarkGroup;
  },
  removeMemberFromMarkGroup: async (groupId: string, memberId: string): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson}`;
    const res = await gql<{ removeMemberFromMarkGroup: MarkGroup }>(`mutation($gid:ID!,$mid:ID!){removeMemberFromMarkGroup(groupId:$gid,memberId:$mid){${f}}}`, { gid: groupId, mid: memberId });
    return res.removeMemberFromMarkGroup;
  },
  updateMarkGroupPermissions: async (groupId: string, permissions: MarkGroupPermissions): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson}`;
    const res = await gql<{ updateMarkGroupPermissions: MarkGroup }>(`mutation($gid:ID!,$input:MarkGroupPermissionsInput!){updateMarkGroupPermissions(groupId:$gid,input:$input){${f}}}`, { gid: groupId, input: permissions });
    return res.updateMarkGroupPermissions;
  },
  createDefaultRoleGroups: async (markId: string): Promise<MarkGroup[]> => {
    const res = await gql<{ createDefaultRoleGroups: MarkGroup[] }>(`mutation($markId:ID!){createDefaultRoleGroups(markId:$markId){id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum}}}`, { markId });
    return res.createDefaultRoleGroups;
  },
  setJaktGroup: async (jaktId: string, groupId: string | null): Promise<void> => {
    await gql<{ setJaktGroup: boolean }>(`mutation($jaktId:ID!,$groupId:ID){setJaktGroup(jaktId:$jaktId,groupId:$groupId)}`, { jaktId, groupId });
  },

  // ── JAKTKLUBB ─────────────────────────────────────────────────
  toggleJaktklubb: async (groupId: string, isJaktklubb: boolean): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail}`;
    const res = await gql<{ toggleJaktklubb: MarkGroup }>(`mutation($gid:ID!,$v:Boolean!){toggleJaktklubb(groupId:$gid,isJaktklubb:$v){${f}}}`, { gid: groupId, v: isJaktklubb });
    return res.toggleJaktklubb;
  },

  setJaktklubSettings: async (groupId: string, input: Pick<JaktklubSettings, 'maxJakttillfallen' | 'description' | 'kontaktEmail' | 'arsKostnad'>): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson}`;
    const res = await gql<{ setJaktklubSettings: MarkGroup }>(`mutation($gid:ID!,$input:SetJaktklubSettingsInput!){setJaktklubSettings(groupId:$gid,input:$input){${f}}}`, { gid: groupId, input });
    return res.setJaktklubSettings;
  },

  publishJaktklubb: async (groupId: string, publish: boolean): Promise<MarkGroup> => {
    const f = `id markId name color memberIds createdAt permissions{canManageCalendar canStartHunt canRecordKill canChat canInviteMembers canManageMeatSharing canPayFees canViewMap canEditMap canManageShootingPlan canViewAlbum canUploadAlbum} jaktklubb{isJaktklubb maxJakttillfallen isPublished publishedAt description kontaktEmail}`;
    const res = await gql<{ publishJaktklubb: MarkGroup }>(`mutation($gid:ID!,$pub:Boolean!){publishJaktklubb(groupId:$gid,publish:$pub){${f}}}`, { gid: groupId, pub: publish });
    return res.publishJaktklubb;
  },

  jaktklubbar: async (): Promise<JaktklubAd[]> => {
    const res = await gql<{ jaktklubbar: JaktklubAd[] }>(`query{jaktklubbar{groupId markId markName groupName groupColor imageUrl memberCount county municipality publishedAt settings{isJaktklubb maxJakttillfallen isPublished description kontaktEmail arsKostnad fallavgiftTyp fallavgiftKr kottPrisPerKg jagarKravJson} costModel{arsavgift medlemKostnadPerJakt fallavgifter{viltslag kon typ kostnad} kottkostnad{viltslag kostnadPerKg}} requirements{minExperienceYears requiresWeaponLicense requiredWeaponTypes requiresDog minHunterYears afterhuntRequired customRequirements{label required}}}}`);
    return res.jaktklubbar ?? [];
  },

  applyToJaktklubb: async (groupId: string, message?: string): Promise<JaktklubApplication> => {
    const res = await gql<{ applyToJaktklubb: JaktklubApplication }>(`mutation($gid:ID!,$msg:String){applyToJaktklubb(groupId:$gid,message:$msg){id groupId markId applicantEmail applicantName message status createdAt}}`, { gid: groupId, msg: message });
    return res.applyToJaktklubb;
  },

  jaktklubApplications: async (groupId: string): Promise<JaktklubApplication[]> => {
    const res = await gql<{ jaktklubApplications: JaktklubApplication[] }>(`query($gid:ID!){jaktklubApplications(groupId:$gid){id groupId markId applicantEmail applicantName message status createdAt handledAt handledByEmail}}`, { gid: groupId });
    return res.jaktklubApplications ?? [];
  },

  myJaktklubApplications: async (): Promise<JaktklubApplication[]> => {
    const res = await gql<{ myJaktklubApplications: JaktklubApplication[] }>(`query{myJaktklubApplications{id groupId markId applicantEmail applicantName message status createdAt handledAt}}`);
    return res.myJaktklubApplications ?? [];
  },

  handleJaktklubApplication: async (applicationId: string, action: 'approved' | 'rejected'): Promise<JaktklubApplication> => {
    const res = await gql<{ handleJaktklubApplication: JaktklubApplication }>(`mutation($id:ID!,$action:String!){handleJaktklubApplication(applicationId:$id,action:$action){id groupId markId applicantEmail applicantName message status createdAt handledAt handledByEmail}}`, { id: applicationId, action });
    return res.handleJaktklubApplication;
  },

  // Mark owner contracts (Erbjud jakt flow)
  submitMarkOwnerApplication: async (input: {
    markName: string;
    fastighetsBeteckning?: string;
    areaHa: number;
    county?: string;
    municipality?: string;
    contactPhone?: string;
    signatureName: string;
    avskjutningsplanJson: string;
    calculatorJson: string;
  }): Promise<MarkOwnerContract> => {
    const MOC_FIELDS = 'id markId registrantEmail registrantName fastighetsBeteckning markName areaHa county contactPhone avskjutningsplanJson calculatorJson signatureName signedAt adminSignatureName adminSignedAt status createdAt';
    const res = await gql<{ submitMarkOwnerApplication: MarkOwnerContract }>(
      `mutation($input:SubmitMarkOwnerApplicationInput!){submitMarkOwnerApplication(input:$input){${MOC_FIELDS}}}`,
      { input }
    );
    return res.submitMarkOwnerApplication;
  },
  myMarkOwnerContracts: async (): Promise<MarkOwnerContract[]> => {
    const MOC_FIELDS = 'id markId registrantEmail registrantName fastighetsBeteckning markName areaHa county contactPhone avskjutningsplanJson calculatorJson signatureName signedAt adminSignatureName adminSignedAt status createdAt';
    const res = await gql<{ myMarkOwnerContracts: MarkOwnerContract[] }>(`query{myMarkOwnerContracts{${MOC_FIELDS}}}`);
    return res?.myMarkOwnerContracts ?? [];
  },
  markOwnerContracts: async (status?: string): Promise<MarkOwnerContract[]> => {
    const MOC_FIELDS = 'id markId registrantEmail registrantName fastighetsBeteckning markName areaHa county contactPhone avskjutningsplanJson calculatorJson signatureName signedAt adminSignatureName adminSignedAt status createdAt';
    const res = await gql<{ markOwnerContracts: MarkOwnerContract[] }>(
      `query($status:String){markOwnerContracts(status:$status){${MOC_FIELDS}}}`,
      { status }
    );
    return res?.markOwnerContracts ?? [];
  },
  adminSignMarkOwnerContract: async (contractId: string, signatureName: string): Promise<MarkOwnerContract> => {
    const MOC_FIELDS = 'id markId registrantEmail registrantName fastighetsBeteckning markName areaHa county contactPhone avskjutningsplanJson calculatorJson signatureName signedAt adminSignatureName adminSignedAt status createdAt';
    const res = await gql<{ adminSignMarkOwnerContract: MarkOwnerContract }>(
      `mutation($contractId:ID!,$signatureName:String!){adminSignMarkOwnerContract(contractId:$contractId,signatureName:$signatureName){${MOC_FIELDS}}}`,
      { contractId, signatureName }
    );
    return res.adminSignMarkOwnerContract;
  },

  markInviteLinks: (markId: string) =>
    gql<{ markInviteLinks: MarkInviteLink[] }>(`query InvLinks($markId: ID!) { markInviteLinks(markId: $markId) { id markId token label role createdBy createdAt expiresAt maxUses useCount revokedAt isValid groupId groupName } }`, { markId }).then(d => d.markInviteLinks),

  inviteLinkPreview: (token: string) =>
    gql<{ inviteLinkPreview: InviteLinkPreview }>(`query InvPreview($token: String!) { inviteLinkPreview(token: $token) { markId markName role isValid alreadyMember } }`, { token }).then(d => d.inviteLinkPreview),

  createMarkInviteLink: (markId: string, input: { label?: string; role?: string; maxUses?: number; expiresAt?: string; groupId?: string | null }) =>
    gql<{ createMarkInviteLink: MarkInviteLink }>(`mutation CreateInvLink($markId: ID!, $input: CreateMarkInviteLinkInput!) { createMarkInviteLink(markId: $markId, input: $input) { id markId token label role createdBy createdAt expiresAt maxUses useCount revokedAt isValid groupId groupName } }`, { markId, input }).then(d => d.createMarkInviteLink),

  revokeMarkInviteLink: (id: string) =>
    gql<{ revokeMarkInviteLink: boolean }>(`mutation RevokeInvLink($id: ID!) { revokeMarkInviteLink(id: $id) }`, { id }).then(d => d.revokeMarkInviteLink),

  redeemMarkInviteLink: (token: string, name: string) =>
    gql<{ redeemMarkInviteLink: RedeemInviteResult }>(`mutation RedeemInvLink($token: String!, $name: String!) { redeemMarkInviteLink(token: $token, name: $name) { markId markName role alreadyMember } }`, { token, name }).then(d => d.redeemMarkInviteLink),

  sendMarkChatMessage: (markId: string, message?: string, imageUrl?: string, messageType?: string, fileUrl?: string, fileName?: string, fileType?: string) =>
    gql<{ sendMarkChatMessage: MarkChatMessage[] | MarkChatMessage }>(`mutation SendChat($markId: ID!, $message: String, $imageUrl: String, $fileUrl: String, $fileName: String, $fileType: String, $messageType: String) { sendMarkChatMessage(markId: $markId, message: $message, imageUrl: $imageUrl, fileUrl: $fileUrl, fileName: $fileName, fileType: $fileType, messageType: $messageType) { id markId senderEmail senderName message imageUrl fileUrl fileName fileType messageType createdAt } }`, { markId, message, imageUrl, messageType, fileUrl, fileName, fileType }).then(d => {
      const r = d.sendMarkChatMessage as MarkChatMessage[] | MarkChatMessage | undefined | null;
      if (Array.isArray(r)) return r;
      if (r && typeof r === 'object' && 'id' in r) return [r];
      return [];
    }),

  // ─── J1 · Planera jakt och inbjudan ───
  planHunt: (markId: string, input: PlanHuntInput, sendEmail = true) =>
    gql<{ planHunt: MarkCalendarEvent }>(`mutation PlanHunt($markId: ID!, $input: PlanHuntInput!, $sendEmail: Boolean) { planHunt(markId: $markId, input: $input, sendEmail: $sendEmail) { ${CAL_EVENT_FIELDS} } }`, { markId, input, sendEmail }).then(d => d.planHunt),
  updatePlannedHunt: (eventId: string, input: PlanHuntInput, notify: boolean) =>
    gql<{ updatePlannedHunt: MarkCalendarEvent }>(`mutation UpdatePlannedHunt($eventId: ID!, $input: PlanHuntInput!, $notify: Boolean) { updatePlannedHunt(eventId: $eventId, input: $input, notify: $notify) { ${CAL_EVENT_FIELDS} } }`, { eventId, input, notify }).then(d => d.updatePlannedHunt),
  cancelPlannedHunt: (eventId: string, notify: boolean) =>
    gql<{ cancelPlannedHunt: MarkCalendarEvent }>(`mutation CancelPlannedHunt($eventId: ID!, $notify: Boolean) { cancelPlannedHunt(eventId: $eventId, notify: $notify) { ${CAL_EVENT_FIELDS} } }`, { eventId, notify }).then(d => d.cancelPlannedHunt),
  huntInvitation: (token: string) =>
    gql<{ huntInvitation: HuntInvitation | null }>(`query HuntInvitation($token: String!) { huntInvitation(token: $token) { ${HUNT_INVITATION_FIELDS} } }`, { token }).then(d => d.huntInvitation),
  huntInvitationForEvent: (eventId: string) =>
    gql<{ huntInvitationForEvent: HuntInvitation | null }>(`query HuntInvitationForEvent($eventId: ID!) { huntInvitationForEvent(eventId: $eventId) { ${HUNT_INVITATION_FIELDS} } }`, { eventId }).then(d => d.huntInvitationForEvent),
  respondToHuntInvitation: (token: string, status: 'yes' | 'maybe' | 'no', comment?: string | null) =>
    gql<{ respondToHuntInvitation: HuntInvitation }>(`mutation RespondHunt($token: String!, $status: String!, $comment: String) { respondToHuntInvitation(token: $token, status: $status, comment: $comment) { ${HUNT_INVITATION_FIELDS} } }`, { token, status, comment }).then(d => d.respondToHuntInvitation),
  /** S1: såternas beläggning dagarna from–to (YYYY-MM-DD). */
  markSatOccupancy: (markId: string, from: string, to?: string) =>
    gql<{ markSatOccupancy: SatOccupancy[] }>(`query($markId: ID!, $from: String!, $to: String) { markSatOccupancy(markId: $markId, from: $from, to: $to) { key kind title startDate endDate startTime endTime status satIds wholeMark } }`, { markId, from, to: to ?? null }).then(d => d.markSatOccupancy ?? []),
  markSatTemplates: (markId: string) =>
    gql<{ markSatTemplates: SatTemplate[] }>(`query($markId: ID!) { markSatTemplates(markId: $markId) { id name planJson createdAt } }`, { markId }).then(d => d.markSatTemplates ?? []),
  saveMarkSatTemplate: (markId: string, name: string, planJson: string) =>
    gql<{ saveMarkSatTemplate: SatTemplate }>(`mutation($markId: ID!, $name: String!, $planJson: String!) { saveMarkSatTemplate(markId: $markId, name: $name, planJson: $planJson) { id name planJson createdAt } }`, { markId, name, planJson }).then(d => d.saveMarkSatTemplate),
  deleteMarkSatTemplate: (id: string) =>
    gql<{ deleteMarkSatTemplate: boolean }>(`mutation($id: ID!) { deleteMarkSatTemplate(id: $id) }`, { id }).then(d => d.deleteMarkSatTemplate),

  createMarkCalendarEvent: (markId: string, input: { title: string; description?: string; eventType?: string; startTime: string; endTime?: string; location?: string; invites?: { email: string; name: string; inviteType?: string }[]; createQmeetMeeting?: boolean; satPlanJson?: string }) =>
    gql<{ createMarkCalendarEvent: MarkCalendarEvent }>(`mutation CreateCal($markId: ID!, $input: CreateMarkCalendarEventInput!) { createMarkCalendarEvent(markId: $markId, input: $input) { id markId title description eventType startTime endTime location createdBy createdAt qmeetMeetingId status endedAt recordingUrl transcription summary observationsJson killsJson satPlanJson invites { id email name inviteType status } } }`, { markId, input }).then(d => d.createMarkCalendarEvent),

  completeHunt: (eventId: string, input: { recordingUrl?: string; transcription?: string; summary?: string; observationsJson?: string; killsJson?: string }) =>
    gql<{ completeHunt: MarkCalendarEvent }>(`mutation CompleteHunt($eventId: ID!, $input: CompleteHuntInput!) { completeHunt(eventId: $eventId, input: $input) { id markId title description eventType startTime endTime location createdBy createdAt qmeetMeetingId status endedAt recordingUrl transcription summary observationsJson killsJson invites { id email name inviteType status } } }`, { eventId, input }).then(d => d.completeHunt),

  huntMedia: (markId: string, from: number, to: number) =>
    gql<{ huntMedia: HuntMedia[] }>(`query HuntMedia($markId: ID!, $from: Float!, $to: Float!) { huntMedia(markId: $markId, from: $from, to: $to) { ${HUNT_MEDIA_FIELDS} } }`, { markId, from, to }).then(d => d.huntMedia),

  /** A3: markens gräns och kartpunkt, för att se vilka bilder som togs på marken. */
  markArea: (id: string) =>
    gql<{ mark: Pick<Mark, 'id' | 'name' | 'coordinatesJson' | 'mapLat' | 'mapLng'> | null }>(`query MarkArea($id: ID!) { mark(id: $id) { id name coordinatesJson mapLat mapLng } }`, { id }).then(d => d.mark),
  addHuntMedia: (markId: string, eventId: string | null, items: HuntMediaInput[]) =>
    gql<{ addHuntMedia: HuntMedia[] }>(`mutation AddHuntMedia($markId: ID!, $eventId: ID, $items: [HuntMediaInput!]!) { addHuntMedia(markId: $markId, eventId: $eventId, items: $items) { ${HUNT_MEDIA_FIELDS} } }`, { markId, eventId, items }).then(d => d.addHuntMedia),

  // ── Pågående jakt (servern speglar LiveKit för Apple Watch) ──
  /** Långlivat token för Apple Watch-appen (ersätter tidigare klocktoken). Se watchBridge.ts. */
  // ── N1 · Pushnotiser (push.ts) ──
  registerPushDevice: (token: string, platform: string, appVersion?: string | null) =>
    gql<{ registerPushDevice: boolean }>(`mutation($t:String!,$p:String,$v:String){registerPushDevice(token:$t,platform:$p,appVersion:$v)}`, { t: token, p: platform, v: appVersion ?? null }).then(d => d.registerPushDevice),
  unregisterPushDevice: (token: string) =>
    gql<{ unregisterPushDevice: boolean }>(`mutation($t:String!){unregisterPushDevice(token:$t)}`, { t: token }).then(d => d.unregisterPushDevice),
  setPushPresence: (token: string, chat: string | null) =>
    gql<{ setPushPresence: boolean }>(`mutation($t:String!,$c:String){setPushPresence(token:$t,chat:$c)}`, { t: token, c: chat }).then(d => d.setPushPresence),
  pushSettings: () =>
    gql<{ pushSettings: PushSettings }>(`{pushSettings{${PUSH_SETTINGS_FIELDS}}}`).then(d => d.pushSettings),
  updatePushSettings: (input: Partial<Omit<PushSettings, 'devices'>>) =>
    gql<{ updatePushSettings: PushSettings }>(`mutation($i:PushSettingsInput!){updatePushSettings(input:$i){${PUSH_SETTINGS_FIELDS}}}`, { i: input }).then(d => d.updatePushSettings),
  createWatchToken: (device?: string) =>
    gql<{ createWatchToken: string }>(`mutation CreateWatchToken($device: String) { createWatchToken(device: $device) }`, { device }).then(d => d.createWatchToken),

  activeMarkHunt: (markId: string) =>
    gql<{ activeMarkHunt: MarkHunt | null }>(`query ActiveMarkHunt($markId: ID!) { activeMarkHunt(markId: $markId) { ${MARK_HUNT_FIELDS} } }`, { markId }).then(d => d.activeMarkHunt),

  markHunts: (markId: string, meetingIds: string[]) =>
    gql<{ markHunts: MarkHunt[] }>(`query MarkHunts($markId: ID!, $meetingIds: [ID!]!) { markHunts(markId: $markId, meetingIds: $meetingIds) { ${MARK_HUNT_FIELDS} } }`, { markId, meetingIds }).then(d => d.markHunts),
  startMarkHunt: (markId: string, withVoice = true) =>
    gql<{ startMarkHunt: MarkHuntSession }>(`mutation StartMarkHunt($markId: ID!, $withVoice: Boolean) { startMarkHunt(markId: $markId, withVoice: $withVoice) { ${HUNT_SESSION_FIELDS} } }`, { markId, withVoice }).then(d => d.startMarkHunt),

  joinMarkHunt: (markId: string, meetingId: string, withVoice = true): Promise<MarkHuntSession> => guestFor(meetingId)
    ? guestCall<{ meetingId: string; markId: string; title: string }>('/hunt', { headers: tokenHeader() })
      .then(h => ({ hunt: { meetingId: h.meetingId, markId: h.markId, title: h.title } as MarkHunt, livekitToken: null, livekitUrl: null, identity: null }))
    : gql<{ joinMarkHunt: MarkHuntSession }>(`mutation JoinMarkHunt($markId: ID!, $meetingId: ID!, $withVoice: Boolean) { joinMarkHunt(markId: $markId, meetingId: $meetingId, withVoice: $withVoice) { ${HUNT_SESSION_FIELDS} } }`, { markId, meetingId, withVoice }).then(d => d.joinMarkHunt),

  /** Tar bort telefonens position ur jakten; klockans (om den är med) syns då för de andra. */
  leaveMarkHunt: (meetingId: string): Promise<boolean> => guestFor(meetingId)
    ? guestCall<{ ok: boolean }>('/hunt/leave', { body: {}, headers: tokenHeader() }).then(() => true)
    : gql<{ leaveMarkHunt: boolean }>(`mutation LeaveMarkHunt($meetingId: ID!) { leaveMarkHunt(meetingId: $meetingId, source: "app") }`, { meetingId }).then(d => d.leaveMarkHunt),

  endMarkHunt: (meetingId: string) =>
    gql<{ endMarkHunt: boolean }>(`mutation EndMarkHunt($meetingId: ID!) { endMarkHunt(meetingId: $meetingId) }`, { meetingId }).then(d => d.endMarkHunt),

  reportHuntPosition: (meetingId: string, p: { lat: number; lng: number; accuracy?: number; heading?: number; avatarUrl?: string }): Promise<boolean> => guestFor(meetingId)
    ? guestCall<{ ok: boolean }>('/hunt/position', { body: { lat: p.lat, lng: p.lng, accuracy: p.accuracy, heading: p.heading }, headers: tokenHeader() }).then(() => true)
    : gql<{ reportHuntPosition: boolean }>(`mutation ReportHuntPosition($meetingId: ID!, $lat: Float!, $lng: Float!, $accuracy: Float, $heading: Float, $avatarUrl: String) { reportHuntPosition(meetingId: $meetingId, lat: $lat, lng: $lng, accuracy: $accuracy, heading: $heading, avatarUrl: $avatarUrl, source: "app") }`, { meetingId, ...p }).then(d => d.reportHuntPosition),

  huntDogPositions: (meetingId: string): Promise<HuntDogPosition[]> => guestFor(meetingId)
    ? guestCall<HuntDogPosition[]>('/hunt/dogs', { headers: tokenHeader() })
    : gql<{ huntDogPositions: HuntDogPosition[] }>(`query HuntDogPositions($meetingId: ID!) { huntDogPositions(meetingId: $meetingId) { dogId dogName ownerEmail ownerName lat lng accuracy battery barkingAt updatedAt isMine powerMode mic } }`, { meetingId }).then(d => d.huntDogPositions),

  /** Jägarnas och hundarnas spår under jakten sedan `since` (ms), högst ett dygn bakåt. points = [lat, lng, ms]. */
  huntTracks: (meetingId: string, since: number): Promise<HuntTrack[]> => guestFor(meetingId)
    ? guestCall<HuntTrack[]>(`/hunt/tracks?since=${Math.floor(since)}`, { headers: tokenHeader() })
    : gql<{ huntTracks: HuntTrack[] }>(`query HuntTracks($meetingId: ID!, $since: Float!) { huntTracks(meetingId: $meetingId, since: $since) { kind id name isMe points } }`, { meetingId, since }).then(d => d.huntTracks),

  /** Dina hundar med hundklockans inställningar. */
  myDogWatches: () =>
    gql<{ myDogWatches: DogWatch[] }>(`query { myDogWatches { dogId name powerMode mic } }`).then(d => d.myDogWatches),

  /** Ägaren styr hundklockan: energispar (var 30:e s) eller jakt (var 10:e s), mikrofon av/på. */
  setDogWatchSettings: (dogId: string, change: { powerMode?: 'hunt' | 'saver'; mic?: boolean }) =>
    gql<{ setDogWatchSettings: { powerMode: 'hunt' | 'saver'; mic: boolean } }>(`mutation SetDogWatchSettings($dogId: ID!, $powerMode: String, $mic: Boolean) { setDogWatchSettings(dogId: $dogId, powerMode: $powerMode, mic: $mic) { powerMode mic } }`, { dogId, ...change }).then(d => d.setDogWatchSettings),

  huntPositions: (meetingId: string): Promise<HuntPosition[]> => guestFor(meetingId)
    ? guestCall<HuntPosition[]>('/hunt/positions', { headers: tokenHeader() })
    : gql<{ huntPositions: HuntPosition[] }>(`query HuntPositions($meetingId: ID!) { huntPositions(meetingId: $meetingId) { userEmail name avatarUrl lat lng accuracy heading source updatedAt isMe } }`, { meetingId }).then(d => d.huntPositions),

  // KA1: gäster (GU1) varken skickar eller ser status.
  huntParticipantStatus: (meetingId: string): Promise<HuntParticipantStatus[]> => guestFor(meetingId)
    ? Promise.resolve([])
    : gql<{ huntParticipantStatus: HuntParticipantStatus[] }>(`query HuntStatus($meetingId: ID!) { huntParticipantStatus(meetingId: $meetingId) { userEmail identity battery charging steps heartRate speed altitude speakerOn sources updatedAt isMe } }`, { meetingId }).then(d => d.huntParticipantStatus),
  reportHuntStatus: (meetingId: string, input: HuntStatusInput): Promise<boolean> => guestFor(meetingId)
    ? Promise.resolve(false)
    : gql<{ reportHuntStatus: boolean }>(`mutation ReportHuntStatus($meetingId: ID!, $input: HuntStatusInput!) { reportHuntStatus(meetingId: $meetingId, input: $input, source: "app") }`, { meetingId, input }).then(d => d.reportHuntStatus),

  sendHuntChatMessage: (meetingId: string, m: { clientId: string; text?: string; imageUrl?: string; lat?: number; lng?: number }): Promise<HuntChatMessage> => guestFor(meetingId)
    ? guestCall<HuntChatMessage>('/hunt/chat', { body: { clientId: m.clientId, text: m.text, lat: m.lat, lng: m.lng }, headers: tokenHeader() })
    : gql<{ sendHuntChatMessage: HuntChatMessage }>(`mutation SendHuntChat($meetingId: ID!, $clientId: String, $text: String, $imageUrl: String, $lat: Float, $lng: Float) { sendHuntChatMessage(meetingId: $meetingId, clientId: $clientId, text: $text, imageUrl: $imageUrl, lat: $lat, lng: $lng, source: "app") { ${HUNT_CHAT_FIELDS} } }`, { meetingId, ...m }).then(d => d.sendHuntChatMessage),

  huntChatMessages: (meetingId: string, after?: number): Promise<HuntChatMessage[]> => guestFor(meetingId)
    ? guestCall<HuntChatMessage[]>(`/hunt/chat${after != null ? `?after=${after}` : ''}`, { headers: tokenHeader() })
    : gql<{ huntChatMessages: HuntChatMessage[] }>(`query HuntChat($meetingId: ID!, $after: Float) { huntChatMessages(meetingId: $meetingId, after: $after) { ${HUNT_CHAT_FIELDS} } }`, { meetingId, after }).then(d => d.huntChatMessages),

  deleteMarkCalendarEvent: (id: string) =>
    gql<{ deleteMarkCalendarEvent: boolean }>(`mutation DelCal($id: ID!) { deleteMarkCalendarEvent(id: $id) }`, { id }).then(d => d.deleteMarkCalendarEvent),

  markBookingCalendar: (markId: string) =>
    gql<{ mark: { bookingMode: BookingMode; bookingPeriods: MarkBookingPeriod[] } | null }>(`query BookingCal($id: ID!) { mark(id: $id) { bookingMode bookingPeriods { id kind start end note } } }`, { id: markId }).then(d => d.mark),
  setMarkBookingMode: (markId: string, mode: BookingMode) =>
    gql<{ setMarkBookingMode: BookingMode }>(`mutation SetBookingMode($markId: ID!, $mode: String!) { setMarkBookingMode(markId: $markId, mode: $mode) }`, { markId, mode }).then(d => d.setMarkBookingMode),
  addMarkBookingPeriod: (markId: string, input: { kind: MarkBookingPeriod['kind']; start: string; end?: string; note?: string | null }) =>
    gql<{ addMarkBookingPeriod: MarkBookingPeriod }>(`mutation AddBookingPeriod($markId: ID!, $input: MarkBookingPeriodInput!) { addMarkBookingPeriod(markId: $markId, input: $input) { id kind start end note } }`, { markId, input }).then(d => d.addMarkBookingPeriod),
  removeMarkBookingPeriod: (id: string) =>
    gql<{ removeMarkBookingPeriod: boolean }>(`mutation RemoveBookingPeriod($id: ID!) { removeMarkBookingPeriod(id: $id) }`, { id }).then(d => d.removeMarkBookingPeriod),

  /** V2: skjutmenyn vid tidpunkten (standard nu) och positionen (standard markens). wholeDay = hela jaktdagen. */
  markSkjutmeny: (markId: string, opts: { at?: string; lat?: number | null; lng?: number | null; wholeDay?: boolean } = {}) =>
    gql<{ markSkjutmeny: MarkSkjutmeny }>(`query MarkSkjutmeny($markId: ID!, $at: String, $lat: Float, $lng: Float, $wholeDay: Boolean) {
      markSkjutmeny(markId: $markId, at: $at, lat: $lat, lng: $lng, wholeDay: $wholeDay) {
        markId at season county sunrise sunset sunNote kottIngar
        source { title url amendedThrough validTo }
        rows { gameType familyKey familyLabel planned remaining actual status reason notes periods fieldFee feeFrom kgPrice }
        birds { enabled canToggle rows { gameType familyKey familyLabel planned remaining actual status reason notes periods fieldFee feeFrom kgPrice } }
      } }`, { markId, at: opts.at ?? null, lat: opts.lat ?? null, lng: opts.lng ?? null, wholeDay: opts.wholeDay ?? false }).then(d => d.markSkjutmeny),

  markAllowedGame: (markId: string) =>
    gql<{ mark: { allowedGame: AllowedGame[] } | null }>(`query MarkAllowedGame($id: ID!) { mark(id: $id) { allowedGame { familyKey label state openToday } } }`, { id: markId })
      .then(d => d.mark?.allowedGame ?? []),

  /** SM2: slår på/av fågel som grupp i markens skjutmeny. */
  setSkjutmenyBirds: (markId: string, enabled: boolean) =>
    gql<{ setSkjutmenyBirds: boolean }>(`mutation SetSkjutmenyBirds($markId: ID!, $enabled: Boolean!) { setSkjutmenyBirds(markId: $markId, enabled: $enabled) }`, { markId, enabled })
      .then(d => d.setSkjutmenyBirds),

  /** V2: tar bort ett viltslag ur viltvårdsplanen (wholeFamily = hela familjen, även ur markens viltlista). */
  removeShootingPlanGame: (markId: string, season: string, gameType: string, wholeFamily = false) =>
    gql<{ removeShootingPlanGame: Mark }>(`mutation RemovePlanGame($markId: ID!, $season: String!, $gameType: String!, $wholeFamily: Boolean) {
      removeShootingPlanGame(markId: $markId, season: $season, gameType: $gameType, wholeFamily: $wholeFamily) { id gameTypesJson }
    }`, { markId, season, gameType, wholeFamily }).then(d => d.removeShootingPlanGame),

  upsertMarkShootingPlan: (input: { markId: string; season: string; gameType: string; plannedCount: number; actualCount?: number; costPerAnimal?: number; notes?: string }) =>
    gql<{ upsertMarkShootingPlan: MarkShootingPlanItem }>(`mutation UpsertPlan($input: UpsertShootingPlanInput!) { upsertMarkShootingPlan(input: $input) { id season gameType plannedCount actualCount costPerAnimal } }`, { input }).then(d => d.upsertMarkShootingPlan),

  addMarkMeatSharing: (input: { markId: string; season: string; gameType: string; animalNumber: number; shotByEmail?: string; shotByName?: string; shotDate?: string; weightKg?: number; sharedToEmail: string; sharedToName: string; portionKg?: number; notes?: string }) =>
    gql<{ addMarkMeatSharing: MarkMeatSharingItem }>(`mutation AddMeat($input: AddMeatSharingInput!) { addMarkMeatSharing(input: $input) { id gameType animalNumber sharedToName portionKg } }`, { input }).then(d => d.addMarkMeatSharing),

  updateMarkMeatQueue: (markId: string, gameType: string, queue: { memberEmail: string; memberName: string; queuePosition: number }[]) =>
    gql<{ updateMarkMeatQueue: MarkMeatQueueItem[] }>(`mutation UpdateQueue($markId: ID!, $gameType: String!, $queue: [MeatQueueEntryInput!]!) { updateMarkMeatQueue(markId: $markId, gameType: $gameType, queue: $queue) { id gameType memberEmail memberName queuePosition } }`, { markId, gameType, queue }).then(d => d.updateMarkMeatQueue),

  // ── MARK PINS (karta) ──────────────────────────────────────────
  createMarkPin: (input: { markId: string; type: string; lat: number; lng: number; name?: string; description?: string }) =>
    gql<{ createMarkPin: any }>(`mutation($input: CreateMarkPinInput!) { createMarkPin(input: $input) { id markId type lat lng name description createdAt } }`, { input }).then(d => d.createMarkPin),

  markCameras: (markId: string) =>
    gql<{ markCameras: MarkCamera[] }>(`query($markId: ID!) { markCameras(markId: $markId) { ${MARK_CAMERA_FIELDS} } }`, { markId }).then(d => d.markCameras),

  createMarkCamera: (input: { markId: string; name: string; lat: number; lng: number }) =>
    gql<{ createMarkCamera: MarkCamera }>(`mutation($input: CreateMarkCameraInput!) { createMarkCamera(input: $input) { ${MARK_CAMERA_FIELDS} } }`, { input }).then(d => d.createMarkCamera),

  updateMarkCamera: (input: { id: string; name?: string; lat?: number; lng?: number }) =>
    gql<{ updateMarkCamera: MarkCamera }>(`mutation($input: UpdateMarkCameraInput!) { updateMarkCamera(input: $input) { ${MARK_CAMERA_FIELDS} } }`, { input }).then(d => d.updateMarkCamera),

  deleteMarkCamera: (id: string) =>
    gql<{ deleteMarkCamera: boolean }>(`mutation($id: ID!) { deleteMarkCamera(id: $id) }`, { id }).then(d => d.deleteMarkCamera),

  deleteMarkPin: (id: string) =>
    gql<{ deleteMarkPin: boolean }>(`mutation($id: ID!) { deleteMarkPin(id: $id) }`, { id }).then(d => d.deleteMarkPin),

  importMarkData: (markId: string, pins: any[], coordinatesJson?: string) =>
    gql<{ importMarkData: boolean }>(`mutation($markId: ID!, $pins: [CreateMarkPinInput!]!, $coordinatesJson: String) { importMarkData(markId: $markId, pins: $pins, coordinatesJson: $coordinatesJson) }`, { markId, pins, coordinatesJson }).then(d => d.importMarkData),

  markPins: (markId: string) =>
    gql<{ markPins: any[] }>(`query($markId: ID!) { markPins(markId: $markId) { id markId type lat lng name description createdAt } }`, { markId }).then(d => d.markPins),

  createFalltViltPin: (input: { markId: string; lat: number; lng: number; viltslag: string; kon?: string; alder?: string; photoUrl?: string; existingQregistryId?: string; name?: string }) =>
    gql<{ createFalltViltPin: any }>(`mutation($input: CreateFalltViltPinInput!) { createFalltViltPin(input: $input) { id markId type lat lng name viltslag kon alder jagarEmail falltStatus qregistryObjectId photoUrls falltViltEvents { id eventType authorEmail oldStatus newStatus commentText newLat newLng photoUrl createdAt } createdAt } }`, { input }).then(d => d.createFalltViltPin),

  updateFalltViltStatus: (pinId: string, status: string, comment?: string) =>
    gql<{ updateFalltViltStatus: any }>(`mutation($pinId: ID!, $status: String!, $comment: String) { updateFalltViltStatus(pinId: $pinId, status: $status, comment: $comment) { id eventType oldStatus newStatus commentText createdAt } }`, { pinId, status, comment }).then(d => d.updateFalltViltStatus),

  addFalltViltComment: (pinId: string, comment: string, photoUrl?: string) =>
    gql<{ addFalltViltComment: any }>(`mutation($pinId: ID!, $comment: String!, $photoUrl: String) { addFalltViltComment(pinId: $pinId, comment: $comment, photoUrl: $photoUrl) { id eventType commentText photoUrl createdAt } }`, { pinId, comment, photoUrl }).then(d => d.addFalltViltComment),

  moveFalltViltPin: (pinId: string, lat: number, lng: number) =>
    gql<{ moveFalltViltPin: any }>(`mutation($pinId: ID!, $lat: Float!, $lng: Float!) { moveFalltViltPin(pinId: $pinId, lat: $lat, lng: $lng) { id lat lng falltViltEvents { id eventType commentText newLat newLng createdAt } } }`, { pinId, lat, lng }).then(d => d.moveFalltViltPin),

  falltViltPublic: (pinId: string) =>
    gql<{ falltViltPublic: any }>(`query($pinId: ID!) { falltViltPublic(pinId: $pinId) { id type lat lng name viltslag kon alder jagarEmail falltStatus qregistryObjectId photoUrls markId falltViltEvents { id eventType authorEmail oldStatus newStatus commentText newLat newLng photoUrl createdAt } createdAt updatedAt } }`, { pinId }).then(d => d.falltViltPublic),

  // ── MARK ALBUM ──────────────────────────────────────────────────
  markAlbumImages: (markId: string) =>
    gql<{ markAlbumImages: MarkAlbumImage[] }>(`query($markId: ID!) { markAlbumImages(markId: $markId) { id markId imageUrl caption sourceType sourceMessageId uploadedBy isPublic createdAt } }`, { markId }).then(d => d.markAlbumImages),

  markPublicImages: (markId: string) =>
    gql<{ markPublicImages: MarkAlbumImage[] }>(`query($markId: ID!) { markPublicImages(markId: $markId) { id markId imageUrl caption sourceType isPublic createdAt } }`, { markId }).then(d => d.markPublicImages),

  toggleAlbumImagePublic: (id: string, isPublic: boolean) =>
    gql<{ toggleAlbumImagePublic: MarkAlbumImage }>(`mutation($id: ID!, $isPublic: Boolean!) { toggleAlbumImagePublic(id: $id, isPublic: $isPublic) { id isPublic } }`, { id, isPublic }).then(d => d.toggleAlbumImagePublic),

  addAlbumImage: (markId: string, imageUrl: string, caption?: string) =>
    gql<{ addAlbumImage: MarkAlbumImage }>(`mutation($markId: ID!, $imageUrl: String!, $caption: String) { addAlbumImage(markId: $markId, imageUrl: $imageUrl, caption: $caption) { id markId imageUrl caption sourceType isPublic createdAt } }`, { markId, imageUrl, caption }).then(d => d.addAlbumImage),

  deleteAlbumImage: (id: string) =>
    gql<{ deleteAlbumImage: boolean }>(`mutation($id: ID!) { deleteAlbumImage(id: $id) }`, { id }).then(d => d.deleteAlbumImage),

  // ── MEAT ORDERS (viltkött escrow) ──────────────────────────────
  createMeatOrder: (input: {
    sellerName: string; sellerEmail?: string; sellerSwish?: string;
    species: string; cut: string; weightG: number; pricePerKg: number; totalPrice: number;
    productId?: string; deliveryAddress?: string; deliveryNote?: string;
  }) =>
    gql<{ createMeatOrder: MeatOrder }>(`
      mutation CMO($input: CreateMeatOrderInput!) {
        createMeatOrder(input: $input) {
          id buyerEmail sellerName species cut weightG pricePerKg totalPrice status swishPaymentId createdAt
          escrow { status totalAmount platformFeePct platformAmount sellerAmount }
        }
      }`, { input }).then(d => d.createMeatOrder),

  confirmMeatDelivery: (orderId: string) =>
    gql<{ confirmMeatDelivery: MeatOrder }>(`
      mutation CMD($orderId: ID!) {
        confirmMeatDelivery(orderId: $orderId) {
          id status confirmedAt escrow { status sellerAmount releasedAt }
        }
      }`, { orderId }).then(d => d.confirmMeatDelivery),

  cancelMeatOrder: (orderId: string) =>
    gql<{ cancelMeatOrder: MeatOrder }>(`
      mutation CancelMO($orderId: ID!) { cancelMeatOrder(orderId: $orderId) { id status } }
    `, { orderId }).then(d => d.cancelMeatOrder),

  myMeatOrders: () =>
    gql<{ myMeatOrders: MeatOrder[] }>(`
      query {
        myMeatOrders {
          id buyerEmail sellerName species cut weightG pricePerKg totalPrice status
          swishPaymentId confirmedAt createdAt
          escrow { status totalAmount platformFeePct platformAmount sellerAmount releasedAt }
        }
      }`).then(d => d.myMeatOrders),

  // ── MEAT PRODUCTS ──────────────────────────────────────────────
  meatProducts: (species?: string) =>
    gql<{ meatProducts: MeatProduct[] }>(`query MeatP($species: String) { meatProducts(species: $species) { id species cut weightG pricePerKg totalPrice location postalCode shotAt seller imageUrl swish frozen description sellerEmail sellerSwish sellerPhone sellerAddress } }`, { species }).then(d => d.meatProducts),

  deleteMeatProduct: (id: string) =>
    gql<{ deleteMeatProduct: boolean }>(`mutation DelMeat($id: ID!) { deleteMeatProduct(id: $id) }`, { id }).then(d => d.deleteMeatProduct),

  createMeatProduct: (input: Partial<MeatProduct>) =>
    gql<{ createMeatProduct: MeatProduct }>(`mutation CreateMeat($input: CreateMeatProductInput!) { createMeatProduct(input: $input) { id species cut weightG pricePerKg totalPrice location postalCode shotAt seller imageUrl swish frozen description sellerEmail sellerSwish sellerPhone sellerAddress } }`, { input }).then(d => d.createMeatProduct),

  updateMeatProduct: (id: string, input: Partial<MeatProduct>) =>
    gql<{ updateMeatProduct: MeatProduct }>(`mutation UpdateMeat($id: ID!, $input: UpdateMeatProductInput!) { updateMeatProduct(id: $id, input: $input) { id species cut weightG pricePerKg totalPrice location postalCode shotAt seller imageUrl swish frozen description sellerEmail sellerSwish sellerPhone sellerAddress } }`, { id, input }).then(d => d.updateMeatProduct),

  // Stripe Checkout — svaret innehåller sessionUrl som webbläsaren skickas till
  startContractCheckout: (contractId: string) =>
    postJson<{ sessionUrl: string; contractId: string }>('/checkout/contract-session', { contractId }),

  // Köp av annons (P1): beloppet räknas av API:t. Felkoder: login, needs_exam, owner_not_connected, full, already_bought …
  // Med lodging (L1) köps boendet i samma betalning och följer jaktens reservation.
  startAdCheckout: (adId: string, dateStart?: string | null, lodging?: LodgingStay | null) =>
    postJson<{ sessionUrl: string; purchaseId: string }>('/checkout/ad-session', { adId, dateStart: dateStart ?? null, lodging: lodging ?? null }),
  // D5: ansökan utan kort (jaktlag, gratis grupp) – blir en antagning som ett köp. Felkoder som startAdCheckout.
  applyToAd: (adId: string, dateStart?: string | null) =>
    gql<{ applyToAd: AdPurchase }>(`mutation($adId:ID!,$d:String){applyToAd(adId:$adId,dateStart:$d){${PURCHASE_FIELDS}}}`, { adId, d: dateStart ?? null }).then(d => d.applyToAd),
  confirmAdPurchase: (purchaseId: string) =>
    postJson<{ purchase: AdPurchase | null }>('/checkout/ad-confirm', { purchaseId }).then(d => d.purchase),
  adPurchaseQuote: (adId: string, dateStart?: string | null) =>
    gql<{ adPurchaseQuote: AdPurchaseQuote }>(`query($adId:ID!,$d:String){adPurchaseQuote(adId:$adId,dateStart:$d){amount platformFee dateStart dateEnd description spotsLeft}}`, { adId, d: dateStart ?? null }).then(d => d.adPurchaseQuote),
  adBuyInfo: (adId: string, dateStart?: string | null) =>
    gql<{ markAd: AdBuyInfo | null }>(`query($id:ID!,$d:String){markAd(id:$id){buyable buyBlockedReason applyWithoutCard spotsLeft(dateStart:$d)}}`, { id: adId, d: dateStart ?? null }).then(d => d.markAd),
  myAdPurchases: () =>
    gql<{ myAdPurchases: AdPurchase[] }>(`{myAdPurchases{${PURCHASE_FIELDS}}}`).then(d => d.myAdPurchases),
  pendingAdPurchases: (markId: string) =>
    gql<{ pendingAdPurchases: AdPurchase[] }>(`query($markId:ID!){pendingAdPurchases(markId:$markId){${PURCHASE_FIELDS}}}`, { markId }).then(d => d.pendingAdPurchases),
  approveAdPurchase: (id: string) =>
    gql<{ approveAdPurchase: AdPurchase }>(`mutation($id:ID!){approveAdPurchase(id:$id){${PURCHASE_FIELDS}}}`, { id }).then(d => d.approveAdPurchase),
  rejectAdPurchase: (id: string, note?: string | null) =>
    gql<{ rejectAdPurchase: AdPurchase }>(`mutation($id:ID!,$note:String){rejectAdPurchase(id:$id,note:$note){${PURCHASE_FIELDS}}}`, { id, note: note ?? null }).then(d => d.rejectAdPurchase),

  // ── Antagning (P2) och avtal (P3/KO1/KO2) ──
  admissionRequirements: (markId: string) =>
    gql<{ markRequirements: MarkRequirements | null }>(`query($markId:ID!){markRequirements(markId:$markId){id markId minExperienceYears requiresWeaponLicense requiredWeaponTypes requiresDog minHunterYears afterhuntRequired customRequirements{label required} requirementsVisible delegatedReviewerRoles requiresExam requiresShootingTest requiresInsurance createdAt updatedAt}}`, { markId }).then(d => d.markRequirements),
  setAdmissionRequirements: (markId: string, input: Partial<Pick<MarkRequirements, 'minExperienceYears' | 'requiresWeaponLicense' | 'requiresDog' | 'minHunterYears' | 'afterhuntRequired' | 'requiresExam' | 'requiresShootingTest' | 'requiresInsurance' | 'customRequirements'>>) =>
    gql<{ setMarkRequirements: { id: string } }>(`mutation($markId:ID!,$input:MarkRequirementsInput!){setMarkRequirements(markId:$markId,input:$input){id}}`, { markId, input }),
  markAdmissions: (markId: string) =>
    gql<{ markAdmissions: MarkAdmission[] }>(`query($markId:ID!){markAdmissions(markId:$markId){${ADMISSION_DETAIL_FIELDS}}}`, { markId }).then(d => d.markAdmissions),
  myAdmissions: () =>
    gql<{ myAdmissions: MarkAdmission[] }>(`{myAdmissions{${ADMISSION_FIELDS}}}`).then(d => d.myAdmissions),
  bookAdmissionInterview: (id: string, at: number, note?: string | null) =>
    gql<{ bookAdmissionInterview: MarkAdmission }>(`mutation($id:ID!,$at:String!,$note:String){bookAdmissionInterview(id:$id,at:$at,note:$note){${ADMISSION_DETAIL_FIELDS}}}`, { id, at: String(at), note: note ?? null }).then(d => d.bookAdmissionInterview),
  joinAdmissionInterview: (id: string) =>
    gql<{ joinAdmissionInterview: InterviewSession }>(`mutation($id:ID!){joinAdmissionInterview(id:$id){title livekitToken livekitUrl identity}}`, { id }).then(d => d.joinAdmissionInterview),
  approveAdmission: (id: string) =>
    gql<{ approveAdmission: MarkAdmission }>(`mutation($id:ID!){approveAdmission(id:$id){${ADMISSION_DETAIL_FIELDS}}}`, { id }).then(d => d.approveAdmission),
  rejectAdmission: (id: string, note?: string | null) =>
    gql<{ rejectAdmission: MarkAdmission }>(`mutation($id:ID!,$note:String){rejectAdmission(id:$id,note:$note){${ADMISSION_DETAIL_FIELDS}}}`, { id, note: note ?? null }).then(d => d.rejectAdmission),
  sendAdmissionContract: (id: string) =>
    gql<{ sendAdmissionContract: MarkAdmission }>(`mutation($id:ID!){sendAdmissionContract(id:$id){${ADMISSION_DETAIL_FIELDS}}}`, { id }).then(d => d.sendAdmissionContract),
  retryAdmissionJoin: (id: string) =>
    gql<{ retryAdmissionJoin: MarkAdmission }>(`mutation($id:ID!){retryAdmissionJoin(id:$id){${ADMISSION_DETAIL_FIELDS}}}`, { id }).then(d => d.retryAdmissionJoin),
  markSigningDocuments: (markId: string) =>
    gql<{ markSigningDocuments: SigningDocument[] }>(`query($markId:ID!){markSigningDocuments(markId:$markId){${SIGNING_DOC_FIELDS}}}`, { markId }).then(d => d.markSigningDocuments),
  platformAgreementDocuments: () =>
    gql<{ platformAgreementDocuments: SigningDocument[] }>(`{platformAgreementDocuments{${SIGNING_DOC_FIELDS}}}`).then(d => d.platformAgreementDocuments),
  saveSigningTemplate: (markId: string, groupId: string | null, title: string, bodyText: string) =>
    gql<{ saveSigningTemplate: SigningDocument }>(`mutation($markId:ID!,$groupId:ID,$title:String!,$bodyText:String!){saveSigningTemplate(markId:$markId,groupId:$groupId,title:$title,bodyText:$bodyText){${SIGNING_DOC_FIELDS}}}`, { markId, groupId, title, bodyText }).then(d => d.saveSigningTemplate),
  deactivateSigningDocument: (id: string) =>
    gql<{ deactivateSigningDocument: boolean }>(`mutation($id:ID!){deactivateSigningDocument(id:$id)}`, { id }).then(d => d.deactivateSigningDocument),
  platformAgreement: (markId: string) =>
    gql<{ platformAgreement: PlatformAgreementStatus }>(`query($markId:ID!){platformAgreement(markId:$markId){${PLATFORM_AGREEMENT_FIELDS}}}`, { markId }).then(d => d.platformAgreement),
  requestPlatformAgreement: (markId: string) =>
    gql<{ requestPlatformAgreement: PlatformAgreementStatus }>(`mutation($markId:ID!){requestPlatformAgreement(markId:$markId){${PLATFORM_AGREEMENT_FIELDS}}}`, { markId }).then(d => d.requestPlatformAgreement),
  /** KO1/KO2: ladda upp ett kontrakt (Word .docx eller PDF). scope 'platform' bara för Qvenari-admin. */
  uploadSigningDocument: (input: { scope: 'mark' | 'platform'; markId?: string | null; groupId?: string | null; title: string; file: File }) => {
    const fd = new FormData();
    fd.append('scope', input.scope);
    if (input.markId) fd.append('markId', input.markId);
    if (input.groupId) fd.append('groupId', input.groupId);
    fd.append('title', input.title);
    fd.append('file', input.file);
    return signingJson<SigningDocument>('/api/signing/documents', { method: 'POST', headers: authHeaders(), body: fd });
  },
  /** Hämtar en fil som kräver inloggning och öppnar den (signerad PDF, uppladdat kontrakt). */
  openSigningFile: async (kind: 'signed' | 'document', id: string) => {
    const path = kind === 'signed' ? `/api/signing/requests/${encodeURIComponent(id)}/signed.pdf` : `/api/signing/documents/${encodeURIComponent(id)}/file`;
    const res = await fetch(resolveUrl(path), { headers: authHeaders() });
    if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || 'Filen kunde inte hämtas');
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  signingView: (token: string) => signingJson<SigningView>(`/api/signing/${encodeURIComponent(token)}`),
  signingFileUrl: (token: string, which: 'document' | 'signed.pdf') => resolveUrl(`/api/signing/${encodeURIComponent(token)}/${which}`),
  signAgreement: (token: string, input: { method: 'drawn'; name: string; signature: string; accept: boolean }) =>
    signingJson<SigningView>(`/api/signing/${encodeURIComponent(token)}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }),

  // ── Boende (L1) ──
  // EK1: fastighetsbeteckningar och markgräns från fastighetskartan (kräver att man får ändra marken).
  markFastighetsbeteckningar: (markId: string) =>
    gql<{ mark: { fastighetsbeteckningar: string[] } | null }>(`query($id:ID!){mark(id:$id){fastighetsbeteckningar}}`, { id: markId }).then(d => d.mark?.fastighetsbeteckningar ?? []),
  setMarkFastighet: (markId: string, input: { fastighetsbeteckningar?: string[]; coordinatesJson?: string; areaHa?: number }) =>
    gql<{ setMarkFastighet: Pick<Mark, 'id' | 'coordinatesJson' | 'areaHa' | 'mapLat' | 'mapLng'> & { fastighetsbeteckningar: string[] } }>(
      `mutation($markId:ID!,$b:[String!],$c:String,$a:Float){setMarkFastighet(markId:$markId,fastighetsbeteckningar:$b,coordinatesJson:$c,areaHa:$a){id coordinatesJson areaHa mapLat mapLng fastighetsbeteckningar}}`,
      { markId, b: input.fastighetsbeteckningar ?? null, c: input.coordinatesJson ?? null, a: input.areaHa ?? null }).then(d => d.setMarkFastighet),
  markLodgings: (markId: string, includeInactive = false) =>
    gql<{ markLodgings: MarkLodging[] }>(`query($markId:ID!,$all:Boolean){markLodgings(markId:$markId,includeInactive:$all){${LODGING_FIELDS}}}`, { markId, all: includeInactive }).then(d => d.markLodgings),
  saveMarkLodging: (input: MarkLodgingInput) =>
    gql<{ saveMarkLodging: MarkLodging }>(`mutation($input:MarkLodgingInput!){saveMarkLodging(input:$input){${LODGING_FIELDS}}}`, { input }).then(d => d.saveMarkLodging),
  deleteMarkLodging: (id: string) =>
    gql<{ deleteMarkLodging: boolean }>(`mutation($id:ID!){deleteMarkLodging(id:$id)}`, { id }).then(d => d.deleteMarkLodging),
  lodgingAvailability: (lodgingId: string, from: string, to: string) =>
    gql<{ lodgingAvailability: LodgingNight[] }>(`query($id:ID!,$from:String!,$to:String!){lodgingAvailability(lodgingId:$id,from:$from,to:$to){date bedsFree bookable}}`, { id: lodgingId, from, to }).then(d => d.lodgingAvailability),
  lodgingQuote: (s: LodgingStay) =>
    gql<{ lodgingQuote: LodgingQuote }>(`query($id:ID!,$in:String!,$out:String!,$g:Int!,$l:Boolean,$c:Boolean){lodgingQuote(lodgingId:$id,checkIn:$in,checkOut:$out,guests:$g,linens:$l,cleaning:$c){checkIn checkOut nights guests linens cleaning lines{${LODGING_LINE_FIELDS}} amount platformFee available unavailableReason}}`,
      { id: s.lodgingId, in: s.checkIn, out: s.checkOut, g: s.guests, l: s.linens, c: s.cleaning }).then(d => d.lodgingQuote),
  markLodgingBookings: (markId: string) =>
    gql<{ markLodgingBookings: LodgingBooking[] }>(`query($markId:ID!){markLodgingBookings(markId:$markId){${LODGING_BOOKING_FIELDS}}}`, { markId }).then(d => d.markLodgingBookings),
  myLodgingBookings: () =>
    gql<{ myLodgingBookings: LodgingBooking[] }>(`{myLodgingBookings{${LODGING_BOOKING_FIELDS}}}`).then(d => d.myLodgingBookings),
  /** Fristående boende: dras direkt. Felkoder: login, owner_not_connected, full, not_bookable, date_passed … */
  startLodgingCheckout: (s: LodgingStay, returnPath: string) =>
    postJson<{ sessionUrl: string; bookingId: string; amount: number }>('/checkout/lodging-session', { ...s, returnPath }),
  confirmLodgingBooking: (bookingId: string) =>
    postJson<{ booking: LodgingBooking | null }>('/checkout/lodging-confirm', { bookingId }).then(d => d.booking),

  // Stripe Connect för markägaren (Ekonomi-fliken)
  markStripeConnect: (markId: string) =>
    gql<{ mark: { stripeConnect: StripeConnectStatus | null } | null }>(`query($id:ID!){mark(id:$id){stripeConnect{${CONNECT_FIELDS}}}}`, { id: markId }).then(d => d.mark?.stripeConnect ?? null),
  startStripeOnboarding: (markId: string) =>
    gql<{ startStripeOnboarding: string }>(`mutation($markId:ID!){startStripeOnboarding(markId:$markId)}`, { markId }).then(d => d.startStripeOnboarding),
  refreshStripeAccount: (markId: string) =>
    gql<{ refreshStripeAccount: StripeConnectStatus }>(`mutation($markId:ID!){refreshStripeAccount(markId:$markId){${CONNECT_FIELDS}}}`, { markId }).then(d => d.refreshStripeAccount),
  stripeDashboardLink: (markId: string) =>
    gql<{ stripeDashboardLink: string }>(`mutation($markId:ID!){stripeDashboardLink(markId:$markId)}`, { markId }).then(d => d.stripeDashboardLink),

  // Godkänd jägare
  hunterApproval: async (): Promise<HunterApproval> => {
    const res = await fetch(resolveUrl('/api/hunter-approval'), { headers: authHeaders() });
    if (!res.ok) return { status: 'none', note: null, hasExam: false };
    return res.json();
  },
  requestHunterApproval: () => postJson<HunterApproval>('/api/hunter-approval/request'),
  adminHunterApprovals: async (status: 'pending' | 'approved' | 'rejected' = 'pending'): Promise<HunterApprovalRequest[]> => {
    const res = await fetch(resolveUrl(`/api/admin/hunter-approvals?status=${status}`), { headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any).error || 'Kunde inte hämta ansökningar');
    return data as HunterApprovalRequest[];
  },
  decideHunterApproval: (userId: string, decision: 'approved' | 'rejected', note?: string) =>
    postJson<{ userId: string; status: string }>(`/api/admin/hunter-approvals/${encodeURIComponent(userId)}`, { decision, note }),

  /** P2: profilfälten som marken kontrollerar vid antagning (GET/PUT /profile, bara dessa fält). */
  getAdmissionProfile: async (): Promise<AdmissionProfileFields> => {
    const res = await fetch(resolveUrl('/api/profile'), { headers: authHeaders() });
    if (!res.ok) throw new Error('Kunde inte hämta profilen');
    const d = await res.json() as Record<string, any>;
    return { hunter_since_year: d.hunter_since_year ?? null, dog_handler: Number(d.dog_handler) === 1, afterhunt_certified: Number(d.afterhunt_certified) === 1,
      shooting_test: d.shooting_test ?? null, insurance: d.insurance ?? null };
  },
  saveAdmissionProfile: async (f: AdmissionProfileFields): Promise<void> => {
    const res = await fetch(resolveUrl('/api/profile'), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(f) });
    if (!res.ok) throw new Error('Kunde inte spara');
  },

  getMapPreferences: async (): Promise<Record<string, unknown> | null> => {
    const token = getAccessToken();
    if (!token) return null;
    const res = await fetch(resolveUrl('/profile'), {
      headers: { ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' },
    });
    if (!res.ok) return null;
    const data = await res.json() as { map_preferences?: Record<string, unknown> | null };
    return data.map_preferences ?? null;
  },

  saveMapPreferences: (prefs: Record<string, unknown>): void => {
    const token = getAccessToken();
    if (!token) return;
    fetch(resolveUrl('/profile'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders(token), 'X-Api-Key': 'dev-secret' },
      body: JSON.stringify({ map_preferences: prefs }),
    }).catch(() => {});
  },
};

export interface MarkRequirements {
  id: string;
  markId: string;
  minExperienceYears: number;
  requiresWeaponLicense: boolean;
  requiredWeaponTypes: string[];
  requiresDog: boolean;
  minHunterYears: number;
  afterhuntRequired: boolean;
  customRequirements: Array<{ label: string; required: boolean }>;
  requirementsVisible: boolean;
  delegatedReviewerRoles: string[];
  /** P2: jägarexamen (standard på), skjutprov och jaktförsäkring */
  requiresExam?: boolean;
  requiresShootingTest?: boolean;
  requiresInsurance?: boolean;
  createdAt: string;
  updatedAt: string;
}


export interface ContractTemplate {
  id: string;
  markId: string;
  name: string;
  contractText: string;
  amount: number;
  swishNumber?: string;
  isDefault: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}
export interface ApplicationContract {
  id: string;
  applicationId: string;
  markId: string;
  applicantEmail: string;
  contractType: string;           // 'membership' | 'hunt' | 'hunt_package'
  contractText: string;
  amount: number;
  swishNumber?: string;
  status: string;
  signedAt?: string;
  signatureName?: string;
  paymentStatus: string;
  paymentConfirmedAt?: string;
  paymentReference?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Gästjaktsavtal-specific fields
  huntName?: string;              // Name of the specific hunt / hunt package
  huntDates?: string[];           // Array of ISO date strings for hunt sessions
  numHunts?: number;              // Number of hunts in package (1 for single)
  pricePerHunt?: number;          // Per-hunt price (for package display)
}

export interface MarkApplication {
  id: string;
  markId: string;
  applicantEmail: string;
  applicantName?: string;
  applicationText?: string;
  videoUrl?: string;
  profileSnapshot?: string;
  meetsRequirements: boolean;
  status: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
  adminQuestion?: string;
  applicantResponse?: string;
  dispensationRequest?: DispensationRequest;
  contract?: ApplicationContract;
  createdAt: string;
  updatedAt: string;
}

export interface DispensationRequest {
  id: string;
  applicationId: string;
  markId: string;
  requestedBy: string;
  requestedTo: string;
  requestMessage?: string;
  status: string;
  decisionBy?: string;
  decisionAt?: string;
  decisionMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeatProduct {
  id: string;
  species: string;
  cut: string;
  weightG: number;
  pricePerKg: number;
  totalPrice: number;
  location?: string;
  postalCode?: string;
  shotAt?: string;
  seller?: string;
  imageUrl?: string;
  swish: boolean;
  frozen: boolean;
  description?: string;
  sellerEmail?: string;
  sellerSwish?: string;
  sellerPhone?: string;
  sellerAddress?: string;
  createdAt?: string;
}

export interface MeatOrder {
  id: string;
  buyerEmail: string;
  buyerName?: string;
  sellerEmail?: string;
  sellerName: string;
  sellerSwish?: string;
  species: string;
  cut: string;
  weightG: number;
  pricePerKg: number;
  totalPrice: number;
  productId?: string;
  status: string; // pending_payment | paid | shipped | delivered | confirmed | disputed | refunded
  swishPaymentId?: string;
  paymentReference?: string;
  deliveryAddress?: string;
  deliveryNote?: string;
  trackingNumber?: string;
  confirmedAt?: string;
  createdAt: string;
  escrow?: {
    status: string;
    totalAmount: number;
    platformFeePct: number;
    platformAmount: number;
    sellerAmount: number;
    releasedAt?: string;
    releaseCondition?: string;
  };
}

// ─── MCP-kopplingar (OAuth för t.ex. Claude.ai, se apps/api/src/mcp/oauth.ts) ───

export interface McpConnection { id: string; name: string; prefix: string; createdAt: number; lastUsedAt: number | null }

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).error ?? `HTTP ${res.status}`);
  return data as T;
}

export const mcpApi = {
  oauthRequest: (id: string) =>
    fetch(resolveUrl(`/oauth/request/${encodeURIComponent(id)}`)).then(r => jsonOrThrow<{ clientName: string; redirectHost: string }>(r)),
  /** Godkänn/neka — returnerar adressen klienten ska skickas tillbaka till. */
  oauthDecide: (requestId: string, approve: boolean) =>
    fetch(resolveUrl(approve ? '/oauth/approve' : '/oauth/deny'), {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...bearerHeaders() }, body: JSON.stringify({ requestId }),
    }).then(r => jsonOrThrow<{ redirectUrl: string }>(r)).then(d => d.redirectUrl),
  connections: () =>
    fetch(resolveUrl('/api/mcp-tokens'), { headers: bearerHeaders() }).then(r => jsonOrThrow<McpConnection[]>(r)),
  revoke: (id: string) =>
    fetch(resolveUrl(`/api/mcp-tokens/${encodeURIComponent(id)}`), { method: 'DELETE', headers: bearerHeaders() }).then(r => jsonOrThrow<{ ok: true }>(r)),
};
